import { Component, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { PericiasService } from '../../core/services/pericias.service';
import { AnaliticoRealService, RotinaAnaliticaReal, ExecucaoAnaliticaReal, AchadoAnaliticoReal, MatrizEvidenciaReal, LaudoChecklistReal } from '../../core/services/analitico-real.service';
import { Pericia, PericiaPayload, RegistroTexto, Quesito, QuesitoPayload, Profissional, VinculoProfissional, VinculoProfissionalPayload, TarefaPericia, TarefaPericiaPayload, PlanejamentoPayload, ProgramaTrabalho, ProgramaTrabalhoPayload, DocumentoPericia, DocumentoPayload, DiligenciaPericia, DiligenciaPayload, HonorarioEstimativa, HonorarioEstimativaPayload, HonorarioBlocoPayload, RetornoNomeacao, RetornoNomeacaoPayload } from '../../core/models/pericia.model';

type ModoTela = 'visualizar' | 'novo' | 'editar';

interface EtapaFluxo {
  ordem: number;
  nome: string;
  aba: string;
  status: 'Não iniciada' | 'Em andamento' | 'Aguardando' | 'Concluída' | 'Crítica' | 'Bloqueada' | 'Liberada';
  percentual: number;
  resumo: string;
  pendencias: string[];
  proximaAcao?: string;
}

interface FaseFluxoAssistido {
  ordem: number;
  titulo: string;
  aba: string;
  status: 'Liberada' | 'Em andamento' | 'Concluída' | 'Bloqueada' | 'Aguardando' | 'Encerrada';
  decisao: string;
  perguntas: string[];
  proximaAcao: string;
  observacaoNbc: string;
}

type TipoNoBpmn = 'inicio' | 'fim' | 'tarefa' | 'decisao' | 'espera';

interface NoBpmnAssistido {
  codigo: string;
  tipo: TipoNoBpmn;
  titulo: string;
  subtitulo: string;
  aba: string;
  status: 'Liberada' | 'Em andamento' | 'Concluída' | 'Bloqueada' | 'Aguardando' | 'Encerrada';
  linha: number;
  coluna: number;
  x?: number;
  y?: number;
  largura?: number;
  altura?: number;
  decisao: string;
  perguntas: string[];
  proximaAcao: string;
  referencia: string;
}

interface DocumentoPadraoTipo {
  tipoPericia: string;
  tipo_documento: string;
  descricao: string;
  formato: string;
  obrigatorio: boolean;
}

interface TarefaPadraoTipo {
  tipoPericia: string;
  categoria: 'Comum' | 'Específica';
  etapa: string;
  tarefa: string;
  procedimento: string;
  documento_necessario: string;
  prazoDias: number;
  responsavel: string;
}

interface ItemHonorario {
  id: number;
  etapa: string;
  atividade: string;
  horas: number;
  valorHora: number;
  despesas: number;
  status: string;
  observacoes: string;
  origem?: string;
  codigoTarefa?: string;
}

interface ItemEsclarecimento {
  id: number;
  dataPedido: string;
  origem: string;
  pontoQuestionado: string;
  resposta: string;
  prazo: string;
  status: string;
  documentoVinculado: string;
}


@Component({
  selector: 'app-pericias',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './pericias.component.html',
  styleUrl: './pericias.component.css'
})
export class PericiasComponent implements OnInit {
  readonly gruposWorkspaceBase = [
    { titulo: 'Resumo', abas: ['Perícia', 'Fluxo', 'Fluxo Assistido'] },
    { titulo: 'Nomeação', abas: ['Equipe', 'Nomeação'] },
    { titulo: 'Honorários', abas: ['Honorários'] },
    { titulo: 'Planejamento', abas: ['Planejamento', 'Programas/Tarefas', 'Quesitos', 'Cronograma'] },
    { titulo: 'Execução', abas: ['Documentos', 'Diligências', 'Execuções', 'Achados', 'Evidências'] },
    { titulo: 'Laudo', abas: ['Checklist', 'Laudo', 'Esclarecimentos'] },
  ];

  readonly abas = this.gruposWorkspaceBase.flatMap(grupo => grupo.abas);
  abaAtiva = signal('Perícia');
  noBpmnSelecionadoCodigo = 'INICIO';
  tipoFluxoBpmn: 'JUDICIAL' | 'ASSISTENTE' | 'EXTRAJUDICIAL' = 'JUDICIAL';
  novaPericiaIniciadaPeloBpmn = false;
  abaPreferidaAoAbrirPericia = 'Perícia';
  pericias = signal<Pericia[]>([]);
  periciaSelecionadaId: number | null = null;
  carregando = signal(false);
  erro = signal('');
  mensagem = signal('');
  modo = signal<ModoTela>('visualizar');

  retornoNomeacaoForm: RetornoNomeacaoPayload = this.retornoNomeacaoVazio();
  retornoNomeacaoSalvando = false;
  retornoNomeacaoPreviewHtml = '';
  retornoNomeacaoPreviewAberto = false;
  retornoNomeacaoFormatoExportacao = 'pdf';
  readonly statusNomeacaoOpcoes = ['Em análise', 'Aceita', 'Não aceita — impedimento', 'Não aceita — suspeição', 'Não aceita — ausência de especialidade técnica', 'Não aceita — impossibilidade de prazo', 'Não aceita — conflito de interesse', 'Não aceita — outro motivo justificado'];
  readonly tiposRetornoNomeacao = ['Aguardando análise', 'Aceite da nomeação', 'Escusa por falta de especialidade técnica', 'Escusa por impossibilidade de prazo', 'Impedimento/Suspeição', 'Conflito de interesse', 'Outro motivo justificado'];
  readonly statusProtocoloRetorno = ['Rascunho', 'Pronto para protocolo', 'Protocolado', 'Cancelado'];


  anoFiltro = new Date().getFullYear().toString();
  mesFiltro = '';
  pesquisaPericia = '';
  mostrarListaPericias = false;
  mostrarPesquisaPericias = true;

  quesitos = signal<Quesito[]>([]);
  carregandoQuesitos = signal(false);
  erroQuesitos = signal('');
  mensagemQuesitos = signal('');
  quesitoEditandoId: number | null = null;
  quesitoFormVisivel = false;
  quesitoForm: QuesitoPayload = this.quesitoVazio();

  readonly origensQuesito = ['Juízo', 'Autor', 'Réu', 'Assistente Técnico', 'Perito', 'Outros'];
  readonly statusQuesito = ['Pendente', 'Em análise', 'Respondido', 'Aguardando documento', 'Prejudicado', 'Indeferido'];

  profissionais = signal<Profissional[]>([]);
  vinculosProfissionais = signal<VinculoProfissional[]>([]);
  vinculoFormVisivel = false;
  vinculoForm: VinculoProfissionalPayload = { profissional_id: 0, papel: 'Perito', observacoes: '' };
  papeisEquipe = ['Perito', 'Assistente Técnico', 'Revisor', 'Apoio', 'Consultor'];

  tarefas = signal<TarefaPericia[]>([]);
  tarefaEditandoId: number | null = null;
  tarefaForm: TarefaPericiaPayload = this.tarefaVazia();
  fasesTarefa = ['Planejamento', 'Quesitos', 'Documentos', 'Diligências', 'Execuções', 'Achados', 'Laudo'];
  statusTarefa = ['Pendente', 'Em andamento', 'Aguardando documento', 'Concluída', 'Atrasada', 'Cancelada'];

  planejamentoForm: PlanejamentoPayload = this.planejamentoVazio();
  statusPlanejamento = ['Em elaboração', 'Aprovado', 'Em execução', 'Concluído', 'Suspenso'];

  honorarios = signal<ItemHonorario[]>([]); // legado preservado, mas a v91.4 usa honorariosEstimativa.
  honorariosEstimativa = signal<HonorarioEstimativa | null>(null);
  salvandoHonorarios = false;
  tipoAtuacaoHonorarios = ['Perito do Juízo', 'Assistente técnico da parte', 'Consultoria extrajudicial', 'Parecer técnico contábil', 'Revisão de cálculos', 'Análise preliminar'];
  modelosEstimativaHonorarios = ['Por blocos de trabalho', 'Percentual sobre valor da ação', 'Valor fixo', 'Hora técnica estimada', 'Modelo misto'];
  complexidadesHonorarios = ['Baixa', 'Média', 'Alta'];
  destinosExportacaoHonorarios = ['juizo', 'assistente', 'extrajudicial'];
  destinoExportacaoHonorariosSelecionado = 'juizo';
  formatoExportacaoHonorariosSelecionado = 'pdf';
  previewHonorariosHtml = '';
  visualizandoPreviewHonorarios = false;
  previewHonorariosTitulo = 'Preview da proposta';
  destinoPreviewHonorarios = 'juizo';
  honorariosForm: HonorarioEstimativaPayload = this.honorariosVazio();
  honorarioEditandoId: number | null = null; // legado v91.1-v91.3 preservado para compatibilidade interna.
  honorarioForm: ItemHonorario = this.honorarioVazio();
  etapasHonorarios = ['Nomeação/aceitação', 'Análise inicial', 'Pesquisa documental', 'Exame contábil/fiscal/societário', 'Planilhas e cálculos', 'Quadros, gráficos e simulações', 'Termos de diligência', 'Elaboração do laudo'];
  statusHonorarios = ['Previsto', 'Em elaboração', 'Protocolado', 'Aprovado', 'Impugnado', 'Ajustado', 'Cancelado'];
  statusHonorariosJudicial = ['Em elaboração', 'Proposta apresentada', 'Aguardando manifestação das partes', 'Impugnada', 'Valor alterado pelo juiz', 'Aprovada/Fixada', 'Depósito pendente', 'Depósito realizado'];
  valorHoraBaseHonorarios = 250;
  valorAcaoHonorarios = 0;
  fatorComplexidadeHonorarios = 1;
  percentualValorAcaoHonorarios = 0;

  esclarecimentos = signal<ItemEsclarecimento[]>([]);
  esclarecimentoEditandoId: number | null = null;
  esclarecimentoForm: ItemEsclarecimento = this.esclarecimentoVazio();
  origensEsclarecimento = ['Juízo', 'Árbitro', 'Autor', 'Réu', 'Assistente técnico', 'Terceiro'];
  statusEsclarecimento = ['Não solicitado', 'Solicitado', 'Em elaboração', 'Protocolado', 'Concluído', 'Cancelado'];

  programas = signal<ProgramaTrabalho[]>([]);
  private adicionandoComunsPadrao = false;
  programaEditandoId: number | null = null;
  programaFormVisivel = false;
  programaForm: ProgramaTrabalhoPayload = this.programaVazio();
  etapasPrograma = ['Análise inicial', 'Documentos', 'Quesitos', 'Cálculos', 'Diligências', 'Achados', 'Laudo'];
  procedimentosPrograma = ['Exame', 'Vistoria', 'Indagação', 'Investigação', 'Arbitramento', 'Avaliação', 'Mensuração', 'Certificação', 'Testabilidade'];

  tarefasComunsPadrao: TarefaPadraoTipo[] = [
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Planejamento', tarefa: 'Conferir cadastro, objeto e prazo', procedimento: 'Exame', documento_necessario: 'Cadastro da perícia, objeto e prazo do laudo', prazoDias: 1, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Planejamento', tarefa: 'Definir equipe', procedimento: 'Planejamento', documento_necessario: 'Cadastro de peritos e assistentes', prazoDias: 1, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Quesitos', tarefa: 'Organizar quesitos', procedimento: 'Exame', documento_necessario: 'Quesitos do juízo, autor, réu e assistentes', prazoDias: 2, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Planejamento', tarefa: 'Definir escopo/planejamento', procedimento: 'Planejamento', documento_necessario: 'Objeto, decisão e limites técnicos', prazoDias: 2, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Documentos', tarefa: 'Adicionar documentos padrão', procedimento: 'Exame', documento_necessario: 'Lista padrão conforme tipo de perícia', prazoDias: 3, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Documentos', tarefa: 'Conferir documentos', procedimento: 'Exame', documento_necessario: 'Documentos recebidos e cadastrados', prazoDias: 5, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Diligências', tarefa: 'Cadastrar diligências', procedimento: 'Indagação', documento_necessario: 'Documentos faltantes e pontos pendentes', prazoDias: 6, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Diligências', tarefa: 'Acompanhar diligências', procedimento: 'Indagação', documento_necessario: 'Diligências emitidas', prazoDias: 9, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Execuções', tarefa: 'Registrar execuções analíticas', procedimento: 'Mensuração', documento_necessario: 'Bases, planilhas e documentos validados', prazoDias: 10, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Execuções', tarefa: 'Revisar execuções', procedimento: 'Certificação', documento_necessario: 'Resultados das execuções analíticas', prazoDias: 12, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Achados', tarefa: 'Registrar achados', procedimento: 'Certificação', documento_necessario: 'Resultados revisados e evidências', prazoDias: 13, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Achados', tarefa: 'Revisar achados', procedimento: 'Certificação', documento_necessario: 'Achados cadastrados', prazoDias: 14, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Evidências', tarefa: 'Montar matriz de evidências', procedimento: 'Certificação', documento_necessario: 'Achados, documentos e execuções vinculados', prazoDias: 15, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Laudo', tarefa: 'Registrar conclusões/limitações', procedimento: 'Certificação', documento_necessario: 'Matriz de evidências e achados revisados', prazoDias: 16, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Laudo', tarefa: 'Conferir checklist', procedimento: 'Certificação', documento_necessario: 'Checklist da perícia', prazoDias: 17, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Laudo', tarefa: 'Visualizar/baixar laudo', procedimento: 'Certificação', documento_necessario: 'Prévia do laudo liberada', prazoDias: 18, responsavel: 'Perito' },
    { tipoPericia: '*', categoria: 'Comum', etapa: 'Laudo', tarefa: 'Anexar versão final ajustada', procedimento: 'Certificação', documento_necessario: 'Laudo final revisado', prazoDias: 19, responsavel: 'Perito' }
  ];

  tarefasPadraoPorTipo: TarefaPadraoTipo[] = [
    { tipoPericia: 'Perícia Bancária', categoria: 'Específica', etapa: 'Análise inicial', tarefa: 'Identificar contrato, período controvertido e metodologia de recálculo', procedimento: 'Exame', documento_necessario: 'Contrato bancário e decisão/quesitos', prazoDias: 2, responsavel: 'Perito' },
    { tipoPericia: 'Perícia Bancária', categoria: 'Específica', etapa: 'Documentos', tarefa: 'Conferir contrato, extratos, pagamentos e memória de cálculo do banco', procedimento: 'Exame', documento_necessario: 'Contrato, extratos, comprovantes e planilhas', prazoDias: 5, responsavel: 'Perito' },
    { tipoPericia: 'Perícia Bancária', categoria: 'Específica', etapa: 'Cálculos', tarefa: 'Recalcular evolução do saldo devedor e encargos', procedimento: 'Mensuração', documento_necessario: 'Extratos e memória de cálculo', prazoDias: 10, responsavel: 'Perito' },
    { tipoPericia: 'Perícia Bancária', categoria: 'Específica', etapa: 'Achados', tarefa: 'Registrar divergências de encargos, taxas ou pagamentos', procedimento: 'Certificação', documento_necessario: 'Resultado das execuções analíticas', prazoDias: 13, responsavel: 'Perito' },
    { tipoPericia: 'Perícia Contábil', categoria: 'Específica', etapa: 'Análise inicial', tarefa: 'Analisar decisão, objeto e quesitos contábeis', procedimento: 'Exame', documento_necessario: 'Petição inicial, decisão e quesitos', prazoDias: 2, responsavel: 'Perito' },
    { tipoPericia: 'Perícia Contábil', categoria: 'Específica', etapa: 'Documentos', tarefa: 'Conferir balancetes, razão, diário e comprovantes', procedimento: 'Exame', documento_necessario: 'Balancetes, razão, diário e comprovantes', prazoDias: 7, responsavel: 'Perito' },
    { tipoPericia: 'Perícia Contábil', categoria: 'Específica', etapa: 'Cálculos', tarefa: 'Elaborar análise contábil e memória técnica', procedimento: 'Mensuração', documento_necessario: 'Demonstrações, razão e documentos suporte', prazoDias: 12, responsavel: 'Perito' },
    { tipoPericia: 'Perícia Contábil na Justiça Trabalhista', categoria: 'Específica', etapa: 'Análise inicial', tarefa: 'Ler decisão de liquidação e delimitar verbas deferidas', procedimento: 'Exame', documento_necessario: 'Sentença, acórdão e quesitos', prazoDias: 2, responsavel: 'Perito' },
    { tipoPericia: 'Perícia Contábil na Justiça Trabalhista', categoria: 'Específica', etapa: 'Documentos', tarefa: 'Conferir contracheques, cartões de ponto e fichas financeiras', procedimento: 'Exame', documento_necessario: 'Contracheques, ponto e fichas financeiras', prazoDias: 6, responsavel: 'Perito' },
    { tipoPericia: 'Perícia Contábil na Justiça Trabalhista', categoria: 'Específica', etapa: 'Cálculos', tarefa: 'Elaborar cálculos trabalhistas e reflexos', procedimento: 'Mensuração', documento_necessario: 'Base salarial, jornada e verbas deferidas', prazoDias: 12, responsavel: 'Perito' },
    { tipoPericia: 'Perícia em Avaliação de Sociedades', categoria: 'Específica', etapa: 'Análise inicial', tarefa: 'Definir data-base, participação societária e critério de avaliação', procedimento: 'Exame', documento_necessario: 'Contrato social, alterações e decisão', prazoDias: 2, responsavel: 'Perito' },
    { tipoPericia: 'Perícia em Avaliação de Sociedades', categoria: 'Específica', etapa: 'Documentos', tarefa: 'Conferir demonstrações contábeis e balancete da data-base', procedimento: 'Exame', documento_necessario: 'Demonstrações e balancetes', prazoDias: 6, responsavel: 'Perito' },
    { tipoPericia: 'Perícia em Avaliação de Sociedades', categoria: 'Específica', etapa: 'Cálculos', tarefa: 'Apurar haveres ou valor da participação societária', procedimento: 'Avaliação', documento_necessario: 'Demonstrações, ajustes e premissas', prazoDias: 14, responsavel: 'Perito' },
    { tipoPericia: 'Perícia Contábil na Área Tributária', categoria: 'Específica', etapa: 'Análise inicial', tarefa: 'Delimitar tributo, período, auto de infração e pontos controvertidos', procedimento: 'Exame', documento_necessario: 'Auto de infração e demonstrativo fiscal', prazoDias: 2, responsavel: 'Perito' },
    { tipoPericia: 'Perícia Contábil na Área Tributária', categoria: 'Específica', etapa: 'Documentos', tarefa: 'Conferir livros fiscais, apurações e guias de recolhimento', procedimento: 'Exame', documento_necessario: 'Livros fiscais, apurações, declarações e guias', prazoDias: 7, responsavel: 'Perito' },
    { tipoPericia: 'Perícia Contábil na Área Tributária', categoria: 'Específica', etapa: 'Cálculos', tarefa: 'Recalcular base, tributo, multas, juros e atualização', procedimento: 'Mensuração', documento_necessario: 'Apurações, legislação e índices aplicáveis', prazoDias: 13, responsavel: 'Perito' },
    { tipoPericia: 'Perícia na Recuperação Judicial e Falência', categoria: 'Específica', etapa: 'Análise inicial', tarefa: 'Identificar fase processual, créditos e documentos da recuperação/falência', procedimento: 'Exame', documento_necessario: 'Decisão, edital, quadro de credores e plano', prazoDias: 2, responsavel: 'Perito' },
    { tipoPericia: 'Perícia na Recuperação Judicial e Falência', categoria: 'Específica', etapa: 'Documentos', tarefa: 'Conferir livros, relação de credores, extratos e documentos financeiros', procedimento: 'Exame', documento_necessario: 'Livros, relação de credores, extratos e plano', prazoDias: 7, responsavel: 'Perito' },
    { tipoPericia: 'Perícia na Recuperação Judicial e Falência', categoria: 'Específica', etapa: 'Cálculos', tarefa: 'Analisar créditos, habilitações, divergências e viabilidade econômico-financeira', procedimento: 'Mensuração', documento_necessario: 'Quadro de credores, documentos suporte e plano', prazoDias: 14, responsavel: 'Perito' },
    { tipoPericia: 'Avaliação de Empresas - Valuation em Perícias', categoria: 'Específica', etapa: 'Análise inicial', tarefa: 'Definir método de valuation e data-base', procedimento: 'Avaliação', documento_necessario: 'Demonstrações e premissas', prazoDias: 2, responsavel: 'Perito' },
    { tipoPericia: 'Avaliação de Empresas - Valuation em Perícias', categoria: 'Específica', etapa: 'Documentos', tarefa: 'Validar demonstrações históricas, projeções e premissas', procedimento: 'Exame', documento_necessario: 'Demonstrações, projeções e premissas', prazoDias: 7, responsavel: 'Perito' },
    { tipoPericia: 'Avaliação de Empresas - Valuation em Perícias', categoria: 'Específica', etapa: 'Cálculos', tarefa: 'Calcular valor econômico e cenários de sensibilidade', procedimento: 'Mensuração', documento_necessario: 'Fluxo de caixa, premissas e taxa de desconto', prazoDias: 15, responsavel: 'Perito' }
  ];

  documentosPericia = signal<DocumentoPericia[]>([]);
  documentoEditandoId: number | null = null;
  documentoFormVisivel = false;
  documentoForm: DocumentoPayload = this.documentoVazio();
  tiposDocumento = ['Extrato bancário', 'Fatura cartão', 'Contrato', 'Comprovante', 'Planilha', 'Laudo', 'Documento judicial', 'Documento fiscal', 'Outros'];
  formatosDocumento = ['PDF', 'XLSX', 'CSV', 'DOCX', 'Imagem', 'TXT', 'Outros'];
  statusDocumento = ['Pendente', 'Recebido', 'Validado', 'Inconsistente', 'Descartado'];

  documentosPadraoPorTipo: DocumentoPadraoTipo[] = [
    // Perícia Contábil
    { tipoPericia: 'Perícia Contábil', tipo_documento: 'Documento judicial', descricao: 'Petição inicial e documentos anexos', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Perícia Contábil', tipo_documento: 'Documento judicial', descricao: 'Contestação, impugnações e manifestações das partes', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Perícia Contábil', tipo_documento: 'Planilha', descricao: 'Balancetes, razão, diário ou demonstrativos contábeis aplicáveis', formato: 'XLSX', obrigatorio: true },
    { tipoPericia: 'Perícia Contábil', tipo_documento: 'Comprovante', descricao: 'Notas fiscais, recibos e comprovantes de pagamento', formato: 'PDF', obrigatorio: false },

    // Perícia Bancária
    { tipoPericia: 'Perícia Bancária', tipo_documento: 'Contrato', descricao: 'Contrato bancário objeto da perícia', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Perícia Bancária', tipo_documento: 'Extrato bancário', descricao: 'Extratos bancários do período discutido', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Perícia Bancária', tipo_documento: 'Planilha', descricao: 'Evolução do saldo devedor / memória de cálculo do banco', formato: 'XLSX', obrigatorio: true },
    { tipoPericia: 'Perícia Bancária', tipo_documento: 'Comprovante', descricao: 'Comprovantes de pagamento, amortização ou liquidação', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Perícia Bancária', tipo_documento: 'Fatura cartão', descricao: 'Faturas e demonstrativos de cartão, se aplicável', formato: 'PDF', obrigatorio: false },

    // Justiça Trabalhista
    { tipoPericia: 'Perícia Contábil na Justiça Trabalhista', tipo_documento: 'Documento judicial', descricao: 'Petição inicial, contestação e sentença/decisão de liquidação', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Perícia Contábil na Justiça Trabalhista', tipo_documento: 'Comprovante', descricao: 'Contracheques, recibos salariais e fichas financeiras', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Perícia Contábil na Justiça Trabalhista', tipo_documento: 'Planilha', descricao: 'Cartões de ponto, escalas e controles de jornada', formato: 'XLSX', obrigatorio: true },
    { tipoPericia: 'Perícia Contábil na Justiça Trabalhista', tipo_documento: 'Contrato', descricao: 'Contrato de trabalho e termos aditivos', formato: 'PDF', obrigatorio: false },

    // Avaliação de Sociedades
    { tipoPericia: 'Perícia em Avaliação de Sociedades', tipo_documento: 'Contrato', descricao: 'Contrato social, alterações contratuais e acordo de sócios', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Perícia em Avaliação de Sociedades', tipo_documento: 'Planilha', descricao: 'Demonstrações contábeis dos últimos exercícios', formato: 'XLSX', obrigatorio: true },
    { tipoPericia: 'Perícia em Avaliação de Sociedades', tipo_documento: 'Planilha', descricao: 'Balancete da data-base da avaliação', formato: 'XLSX', obrigatorio: true },
    { tipoPericia: 'Perícia em Avaliação de Sociedades', tipo_documento: 'Laudo', descricao: 'Laudos, avaliações anteriores ou documentos de valuation existentes', formato: 'PDF', obrigatorio: false },

    // Tributária
    { tipoPericia: 'Perícia Contábil na Área Tributária', tipo_documento: 'Documento fiscal', descricao: 'Autos de infração, notificações fiscais e demonstrativos do crédito tributário', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Perícia Contábil na Área Tributária', tipo_documento: 'Planilha', descricao: 'Livros fiscais, apurações e demonstrativos de bases de cálculo', formato: 'XLSX', obrigatorio: true },
    { tipoPericia: 'Perícia Contábil na Área Tributária', tipo_documento: 'Comprovante', descricao: 'Guias de recolhimento, parcelamentos e comprovantes de pagamento', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Perícia Contábil na Área Tributária', tipo_documento: 'Documento judicial', descricao: 'Processo administrativo ou judicial vinculado ao débito', formato: 'PDF', obrigatorio: false },

    // Recuperação Judicial
    { tipoPericia: 'Perícia na Recuperação Judicial e Falência', tipo_documento: 'Documento judicial', descricao: 'Plano de recuperação judicial, decisões e relatórios do administrador judicial', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Perícia na Recuperação Judicial e Falência', tipo_documento: 'Planilha', descricao: 'Relação de credores e classificação dos créditos', formato: 'XLSX', obrigatorio: true },
    { tipoPericia: 'Perícia na Recuperação Judicial e Falência', tipo_documento: 'Planilha', descricao: 'Demonstrações contábeis e fluxo de caixa projetado', formato: 'XLSX', obrigatorio: true },

    // Atuarial
    { tipoPericia: 'Tópicos de Perícia Atuarial', tipo_documento: 'Planilha', descricao: 'Base de participantes/beneficiários e premissas atuariais', formato: 'XLSX', obrigatorio: true },
    { tipoPericia: 'Tópicos de Perícia Atuarial', tipo_documento: 'Laudo', descricao: 'Nota técnica atuarial, parecer ou cálculo atuarial anterior', formato: 'PDF', obrigatorio: true },

    // Mediação e Arbitragem
    { tipoPericia: 'Perícia, Mediação e Arbitragem Contábil', tipo_documento: 'Contrato', descricao: 'Convenção arbitral, contrato principal e termos de referência', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Perícia, Mediação e Arbitragem Contábil', tipo_documento: 'Documento judicial', descricao: 'Quesitos, manifestações e documentos das partes', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Perícia, Mediação e Arbitragem Contábil', tipo_documento: 'Planilha', descricao: 'Memórias de cálculo, demonstrativos e documentos financeiros', formato: 'XLSX', obrigatorio: true },

    // Valuation
    { tipoPericia: 'Avaliação de Empresas - Valuation em Perícias', tipo_documento: 'Planilha', descricao: 'Demonstrações financeiras históricas', formato: 'XLSX', obrigatorio: true },
    { tipoPericia: 'Avaliação de Empresas - Valuation em Perícias', tipo_documento: 'Planilha', descricao: 'Projeções, premissas e fluxo de caixa', formato: 'XLSX', obrigatorio: true },
    { tipoPericia: 'Avaliação de Empresas - Valuation em Perícias', tipo_documento: 'Contrato', descricao: 'Contrato social, alterações e documentos societários', formato: 'PDF', obrigatorio: true },
    { tipoPericia: 'Avaliação de Empresas - Valuation em Perícias', tipo_documento: 'Laudo', descricao: 'Laudos de avaliação anteriores ou estudos de mercado', formato: 'PDF', obrigatorio: false }
  ];

  arquivoDocumentoSelecionado: File | null = null;

  diligenciasPericia = signal<DiligenciaPericia[]>([]);
  diligenciaEditandoId: number | null = null;
  diligenciaFormVisivel = false;
  diligenciaForm: DiligenciaPayload = this.diligenciaVazia();
  statusDiligencia = ['Pendente', 'Enviada', 'Aguardando resposta', 'Respondida', 'Vencida', 'Cancelada'];


rotinasAnaliticas = signal<RotinaAnaliticaReal[]>([]);
execucoesAnaliticas = signal<ExecucaoAnaliticaReal[]>([]);
achadosAnaliticos = signal<AchadoAnaliticoReal[]>([]);
evidenciasMatriz = signal<MatrizEvidenciaReal[]>([]);
laudosChecklist = signal<LaudoChecklistReal[]>([]);

execucaoFormVisivel = false;
achadoFormVisivel = false;
evidenciaFormVisivel = false;
laudoFormVisivel = false;

execucaoForm: ExecucaoAnaliticaReal = this.execucaoAnaliticaVazia();
achadoAnaliticoForm: AchadoAnaliticoReal = this.achadoAnaliticoVazio();
evidenciaMatrizForm: MatrizEvidenciaReal = this.evidenciaMatrizVazia();
laudoChecklistForm: LaudoChecklistReal = this.laudoChecklistVazio();

laudoWordPreview: any = null;
carregandoLaudoWord = false;
arquivoLaudoSelecionado: File | null = null;
descricaoVersaoLaudo = '';
statusExecucaoAnalitica = ['Registrada', 'Em execução', 'Concluída', 'Erro', 'Cancelada'];
gravidadesAchado = ['Baixa', 'Média', 'Alta', 'Crítica'];
tiposAchadoAnalitico = ['Inconsistência', 'Divergência', 'Omissão', 'Indício', 'Confirmação', 'Limitação'];
statusLaudoChecklist = ['Em elaboração', 'Em revisão', 'Concluído', 'Emitido'];

  readonly mesesFiltro = [
    { valor: '01', nome: 'Janeiro' },
    { valor: '02', nome: 'Fevereiro' },
    { valor: '03', nome: 'Março' },
    { valor: '04', nome: 'Abril' },
    { valor: '05', nome: 'Maio' },
    { valor: '06', nome: 'Junho' },
    { valor: '07', nome: 'Julho' },
    { valor: '08', nome: 'Agosto' },
    { valor: '09', nome: 'Setembro' },
    { valor: '10', nome: 'Outubro' },
    { valor: '11', nome: 'Novembro' },
    { valor: '12', nome: 'Dezembro' }
  ];

  formulario: PericiaPayload = this.formularioVazio();

  readonly tiposPericia = [
    'Perícia Contábil',
    'Perícia Bancária',
    'Perícia Contábil na Justiça Trabalhista',
    'Perícia em Avaliação de Sociedades',
    'Perícia Contábil na Área Tributária',
    'Perícia na Recuperação Judicial e Falência',
    'Tópicos de Perícia Atuarial',
    'Perícia, Mediação e Arbitragem Contábil',
    'Avaliação de Empresas - Valuation em Perícias'
  ];

  readonly statusOpcoes = [
    'Planejamento',
    'A iniciar',
    'Em andamento',
    'Aguardando documentos',
    'Em análise',
    'Em diligência',
    'Minuta do laudo',
    'Concluída',
    'Suspensa',
    'Cancelada'
  ];

  periciaAtual(): Pericia | null {
    return this.pericias().find(p => p.id === this.periciaSelecionadaId) ?? null;
  }

  temPericiaAberta(): boolean {
    return !!this.periciaAtual();
  }


  anoAtual(): string {
    return new Date().getFullYear().toString();
  }

  codigoPreviewItem(prefixo: string): string {
    return `${prefixo}- automático`;
  }

  codigoPreviewNovaPericia(): string {
    const ano = this.anoFiltro || new Date().getFullYear().toString();
    return `PER-${ano}- automático`;
  }

  private limparDadosPericiaSelecionada(): void {
    this.quesitos.set([]);
    this.vinculosProfissionais.set([]);
    this.tarefas.set([]);
    this.programas.set([]);
    this.documentosPericia.set([]);
    this.diligenciasPericia.set([]);
    this.execucoesAnaliticas.set([]);
    this.achadosAnaliticos.set([]);
    this.evidenciasMatriz.set([]);
    this.laudosChecklist.set([]);

    this.quesitoEditandoId = null;
    this.tarefaEditandoId = null;
    this.programaEditandoId = null;
    this.documentoEditandoId = null;
    this.diligenciaEditandoId = null;

    this.execucaoFormVisivel = false;
    this.achadoFormVisivel = false;
    this.evidenciaFormVisivel = false;
    this.laudoFormVisivel = false;

    this.limparFormularioQuesito();
    this.tarefaForm = this.tarefaVazia();
    this.programaForm = this.programaVazio();
    this.programaFormVisivel = false;
    this.cancelarDocumento();
    this.cancelarDiligencia();
    this.execucaoForm = this.execucaoAnaliticaVazia();
    this.achadoAnaliticoForm = this.achadoAnaliticoVazio();
    this.evidenciaMatrizForm = this.evidenciaMatrizVazia();
    this.laudoChecklistForm = this.laudoChecklistVazio();
  }

  constructor(private readonly service: PericiasService, private readonly analiticoReal: AnaliticoRealService, private readonly route: ActivatedRoute) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      const aba = params.get('aba') || params.get('tela') || '';
      const abaNormalizada = aba.toLowerCase();

      if (['fluxo assistido', 'fluxo-bpmn', 'bpmn', 'assistente', 'assistente-bpmn'].includes(abaNormalizada)) {
        this.abaPreferidaAoAbrirPericia = 'Fluxo Assistido';
        this.abaAtiva.set('Fluxo Assistido');
        this.noBpmnSelecionadoCodigo = 'INICIO';
        this.mostrarListaPericias = false;
        this.mostrarPesquisaPericias = false;
      }

      if (['perícia', 'pericia', 'lista', 'workspace'].includes(abaNormalizada)) {
        this.abaPreferidaAoAbrirPericia = 'Perícia';
        this.abaAtiva.set('Perícia');
      }
    });

    this.recarregar();
    this.carregarProfissionais();
  }

  anosDisponiveis(): string[] {
    const anos: string[] = [];
    for (let ano = 2020; ano <= 2099; ano++) {
      anos.push(String(ano));
    }
    return anos;
  }

  periciasFiltradas(): Pericia[] {
    const termo = this.normalizarTexto(this.pesquisaPericia);

    return this.pericias().filter(p => {
      const data = this.extrairAnoMes(p);
      const bateAno = !this.anoFiltro || data.ano === this.anoFiltro;
      const bateMes = !this.mesFiltro || data.mes === this.mesFiltro;
      const textoBusca = this.normalizarTexto([
        p.codigo,
        p.titulo,
        p.numero_processo,
        p.cliente,
        p.tipo_pericia,
        p.status
      ].join(' '));
      const batePesquisa = !termo || textoBusca.includes(termo);
      return bateAno && bateMes && batePesquisa;
    });
  }

  recarregar(): void {
    this.carregando.set(true);
    this.erro.set('');
    this.mensagem.set('');
    this.service.listar().subscribe({
      next: (dados) => {
        this.pericias.set(dados);
        if (this.periciaSelecionadaId && !dados.some(p => p.id === this.periciaSelecionadaId)) {
          this.periciaSelecionadaId = null;
        }
        this.mostrarListaPericias = !this.periciaSelecionadaId;
        this.mostrarPesquisaPericias = !this.periciaSelecionadaId;
        this.carregarFormularioSelecionada();
        if (this.periciaSelecionadaId) {
          this.carregarProgramas();
          this.carregarHonorarios();
        }
        this.carregando.set(false);
      },
      error: () => {
        this.erro.set('Não foi possível carregar as perícias do backend. Verifique se o backend está rodando na porta 8000.');
        this.carregando.set(false);
      }
    });
  }

  nomeacaoAceita(): boolean {
    return this.retornoNomeacaoForm?.status_nomeacao === 'Aceita';
  }

  honorariosLiberamExecucao(): boolean {
    const status = this.honorariosForm?.status_honorarios || this.honorariosEstimativa()?.status_honorarios || '';
    return ['Aprovada/Fixada', 'Depósito pendente', 'Depósito realizado'].includes(status);
  }

  statusHonorariosResumoFluxo(): string {
    if (!this.nomeacaoAceita()) return 'Bloqueado até aceite da nomeação';
    return this.honorariosForm?.status_honorarios || this.honorariosEstimativa()?.status_honorarios || 'Em elaboração';
  }

  gruposWorkspaceVisiveis(): { titulo: string; abas: string[] }[] {
    // Nova perícia: só permite preencher Dados da Perícia até salvar.
    if (this.modo() === 'novo') {
      return [
        { titulo: 'Resumo', abas: ['Perícia'] }
      ];
    }

    // Sem perícia selecionada, mantém apenas o resumo.
    if (!this.periciaAtual()) {
      return [
        { titulo: 'Resumo', abas: ['Perícia', 'Fluxo', 'Fluxo Assistido'] }
      ];
    }

    // Antes do aceite, libera somente dados, fluxo e nomeação.
    // Se a nomeação for recusada, honorários/planejamento/execução/laudo permanecem bloqueados.
    if (!this.nomeacaoAceita()) {
      return [
        { titulo: 'Resumo', abas: ['Perícia', 'Fluxo', 'Fluxo Assistido'] },
        { titulo: 'Nomeação', abas: ['Equipe', 'Nomeação'] }
      ];
    }

    if (!this.honorariosLiberamExecucao()) {
      return [
        { titulo: 'Resumo', abas: ['Perícia', 'Fluxo', 'Fluxo Assistido'] },
        { titulo: 'Nomeação', abas: ['Equipe', 'Nomeação'] },
        { titulo: 'Honorários', abas: ['Honorários'] }
      ];
    }

    return this.gruposWorkspaceBase;
  }

  private abaPermitida(aba: string): boolean {
    return this.gruposWorkspaceVisiveis().some(grupo => grupo.abas.includes(aba));
  }

  private primeiraAbaPermitida(): string {
    return this.gruposWorkspaceVisiveis()[0]?.abas[0] ?? 'Perícia';
  }

  grupoWorkspaceAtual(): string {
    const aba = this.abaAtiva();
    return this.gruposWorkspaceVisiveis().find(grupo => grupo.abas.includes(aba))?.titulo ?? 'Resumo';
  }

  subabasGrupoAtual(): string[] {
    const grupoAtual = this.grupoWorkspaceAtual();
    return this.gruposWorkspaceVisiveis().find(grupo => grupo.titulo === grupoAtual)?.abas ?? [];
  }

  rotuloAbaWorkspace(aba: string): string {
    const rotulos: Record<string, string> = {
      'Perícia': 'Dados da Perícia',
      'Fluxo': 'Fluxo / Pendências',
      'Fluxo Assistido': 'Fluxo BPMN / Assistente',
      'Execuções': 'Execuções Analíticas',
      'Programas/Tarefas': 'Programas / Tarefas',
    };
    return rotulos[aba] ?? aba;
  }

  iconeAbaWorkspace(aba: string): string {
    const icones: Record<string, string> = {
      'Fluxo': '📌',
      'Fluxo Assistido': '🔁',
      'Perícia': '📄',
      'Nomeação': '⚖️',
      'Equipe': '👥',
      'Honorários': '💰',
      'Planejamento': '🧭',
      'Cronograma': '📅',
      'Programas/Tarefas': '✅',
      'Quesitos': '❓',
      'Documentos': '📎',
      'Diligências': '📬',
      'Execuções': '⚙️',
      'Achados': '🔎',
      'Evidências': '🧾',
      'Checklist': '☑️',
      'Laudo': '📝',
      'Esclarecimentos': '💬',
    };
    return icones[aba] ?? '•';
  }

  descricaoAbaWorkspace(aba: string): string {
    const descricoes: Record<string, string> = {
      'Fluxo': 'status, pendências e próximos passos',
      'Fluxo Assistido': 'processo visual orientado pela NBC de perícia',
      'Perícia': 'cadastro e dados principais',
      'Nomeação': 'aceite, escusa e manifestação ao juízo',
      'Equipe': 'perito principal e profissionais vinculados',
      'Honorários': 'proposta, preview e exportação',
      'Planejamento': 'escopo, metodologia e diretrizes',
      'Cronograma': 'prazos e marcos do trabalho',
      'Programas/Tarefas': 'tarefas técnicas da perícia',
      'Quesitos': 'perguntas e pontos controvertidos',
      'Documentos': 'documentos recebidos e anexos',
      'Diligências': 'solicitações e retornos',
      'Execuções': 'rotinas analíticas executadas',
      'Achados': 'pontos identificados na análise',
      'Evidências': 'provas e vínculo com achados',
      'Checklist': 'conferência antes do laudo',
      'Laudo': 'redação e exportação final',
      'Esclarecimentos': 'respostas posteriores ao laudo',
    };
    return descricoes[aba] ?? '';
  }

  selecionarGrupoWorkspace(titulo: string): void {
    const grupo = this.gruposWorkspaceVisiveis().find(item => item.titulo === titulo);
    const primeiraAba = grupo?.abas[0];
    if (primeiraAba) {
      this.selecionarAba(primeiraAba);
    }
  }

  selecionarAba(aba: string): void {
    if (this.modo() !== 'novo' && !this.periciaAtual() && aba !== 'Fluxo Assistido') {
      this.abaAtiva.set('Perícia');
      this.limparDadosPericiaSelecionada();
      return;
    }

    if (!this.abaPermitida(aba)) {
      this.abaAtiva.set(this.primeiraAbaPermitida());
      return;
    }

    this.abaAtiva.set(aba);

    if (this.modo() === 'novo' || !this.periciaAtual()) {
      if (aba !== 'Fluxo Assistido') {
        this.limparDadosPericiaSelecionada();
      }
      return;
    }

    if (aba === 'Nomeação' || aba === 'Fluxo Assistido') {
      this.carregarRetornoNomeacao();
      this.carregarProfissionais();
      this.carregarVinculosProfissionais();
    }
    if (aba === 'Quesitos') {
      this.carregarQuesitos();
    }
    if (aba === 'Equipe') {
      this.carregarProfissionais();
      this.carregarVinculosProfissionais();
    }
    if (aba === 'Planejamento') {
      this.carregarPlanejamento();
    }
    if (aba === 'Programas/Tarefas') {
      this.carregarProgramas();
    }
    if (aba === 'Documentos') {
      this.carregarDocumentos();
    }
    if (aba === 'Diligências') {
      this.carregarDiligencias();
    }
    if (aba === 'Execuções' || aba === 'Achados' || aba === 'Evidências' || aba === 'Laudo') {
      this.carregarAnaliticoReal();
    }
  }

  selecionarPericia(id: number | string | null): void {
    this.periciaSelecionadaId = id ? Number(id) : null;
    this.modo.set('visualizar');
    this.abaAtiva.set(this.abaPreferidaAoAbrirPericia);
    this.limparDadosPericiaSelecionada();
    this.carregarFormularioSelecionada();
  }

  limparFiltros(): void {
    this.anoFiltro = '';
    this.mesFiltro = '';
    this.pesquisaPericia = '';
    this.mostrarListaPericias = true;
  }

  voltarPesquisaPericias(): void {
    this.periciaSelecionadaId = null;
    this.modo.set('visualizar');
    this.abaAtiva.set('Perícia');
    this.mostrarPesquisaPericias = true;
    this.mostrarListaPericias = true;
    this.carregarFormularioSelecionada();
    this.limparDadosPericiaSelecionada();
  }

  aplicarFiltroPericias(): void {
    // A lista não abre automaticamente ao alterar filtros.
    // A pesquisa/listagem só aparece quando clicar na lupa.
    this.mostrarPesquisaPericias = true;
  }

  executarPesquisaPericias(): void {
    this.mostrarPesquisaPericias = true;
    this.mostrarListaPericias = true;
    this.periciaSelecionadaId = null;
    this.modo.set('visualizar');
    this.abaAtiva.set('Perícia');
    this.carregarFormularioSelecionada();
    this.limparDadosPericiaSelecionada();
  }

  alternarListaPericias(): void {
    this.mostrarListaPericias = !this.mostrarListaPericias;

    if (this.mostrarListaPericias) {
      this.periciaSelecionadaId = null;
      this.modo.set('visualizar');
      this.abaAtiva.set('Perícia');
      this.carregarFormularioSelecionada();
      this.limparDadosPericiaSelecionada();
    }
  }

  listarPericias(): void {
    this.mostrarListaPericias = true;
    this.periciaSelecionadaId = null;
    this.modo.set('visualizar');
    this.abaAtiva.set('Perícia');
    this.carregarFormularioSelecionada();
    this.limparDadosPericiaSelecionada();
  }

  abrirPericia(pericia: Pericia): void {
    this.erro.set('');
    this.mensagem.set('');
    this.periciaSelecionadaId = pericia.id;
    this.modo.set('visualizar');
    this.abaAtiva.set(this.abaPreferidaAoAbrirPericia);
    this.mostrarListaPericias = false;
    this.mostrarPesquisaPericias = false;
    this.mostrarPesquisaPericias = false;
    this.limparDadosPericiaSelecionada();
    this.preencherFormulario(pericia);
    this.carregarTodasAbasDaPericia();
  }

  private async carregarTodasAbasDaPericia(): Promise<void> {
    if (!this.periciaSelecionadaId || this.modo() === 'novo') return;

    await this.carregarRetornoNomeacao();
    this.carregarVinculosProfissionais();

    if (!this.nomeacaoAceita()) {
      if (!this.abaPermitida(this.abaAtiva())) {
        this.abaAtiva.set('Perícia');
      }
      return;
    }

    this.carregarQuesitos();
    this.carregarPlanejamento();
    this.carregarProgramas();
    this.carregarDocumentos();
    this.carregarDiligencias();
    this.carregarAnaliticoReal();
    this.carregarHonorarios();
  }

  iniciarNovo(): void {
    this.erro.set('');
    this.mensagem.set('');
    this.periciaSelecionadaId = null;
    this.modo.set('novo');
    this.abaAtiva.set('Perícia');
    this.formulario = this.formularioVazio();
    this.formulario.codigo = '';
    this.mostrarListaPericias = false;
    this.limparDadosPericiaSelecionada();
  }

  iniciarEdicao(): void {
    const p = this.periciaAtual();
    if (!p) return;
    this.modo.set('editar');
    this.abaAtiva.set('Perícia');
    this.carregarFormularioSelecionada();
  }

  cancelarEdicao(): void {
    this.modo.set('visualizar');
    if (!this.periciaSelecionadaId && this.pericias().length) {
      this.periciaSelecionadaId = this.pericias()[0].id;
    }
    this.carregarFormularioSelecionada();
    if (!this.periciaSelecionadaId) {
      this.limparDadosPericiaSelecionada();
    }
  }

  salvar(): void {
    this.erro.set('');
    this.mensagem.set('');
    const payload = { ...this.formulario };
    if (!payload.titulo) {
      this.erro.set('Informe pelo menos o Título.');
      return;
    }

    if (this.modo() === 'novo') {
      payload.codigo = '';
      this.service.criar(payload).subscribe({
        next: (nova) => {
          this.modo.set('visualizar');
          this.periciaSelecionadaId = nova.id;
          this.mostrarListaPericias = false;
          this.mostrarPesquisaPericias = false;
          this.retornoNomeacaoForm = this.retornoNomeacaoVazio();
          if (this.novaPericiaIniciadaPeloBpmn) {
            this.abaAtiva.set('Fluxo Assistido');
            this.noBpmnSelecionadoCodigo = 'ATUACAO';
            this.mensagem.set('Perícia criada pelo assistente. Agora defina o tipo de atuação no losango e avance para Equipe / Papéis.');
            this.novaPericiaIniciadaPeloBpmn = false;
          } else {
            this.abaAtiva.set('Nomeação');
            this.mensagem.set('Salvo.');
          }
          this.recarregar();
        },
        error: () => this.erro.set('Não foi possível criar a perícia.')
      });
      return;
    }

    this.salvarAtual();
  }

  salvarAtual(): void {
    const atual = this.periciaAtual();
    if (!atual) return;
    this.erro.set('');
    this.mensagem.set('');

    const payload = { ...this.formulario };
    if (!payload.titulo) {
      this.erro.set('Informe pelo menos o Título.');
      return;
    }

    this.service.atualizar(atual.id, payload).subscribe({
      next: () => {
        this.mensagem.set('Salvo.');
        this.modo.set('visualizar');
        this.mostrarListaPericias = false;
        this.mostrarPesquisaPericias = true;
        this.recarregar();
      },
      error: () => this.erro.set('Não foi possível atualizar a perícia.')
    });
  }

  excluirAtual(): void {
    const atual = this.periciaAtual();
    if (!atual) return;
    if (!confirm(`Excluir a perícia ${atual.codigo} - ${atual.titulo}?`)) return;
    this.service.excluir(atual.id).subscribe({
      next: () => {
        this.mensagem.set('Perícia excluída com sucesso.');
        this.periciaSelecionadaId = null;
        this.limparDadosPericiaSelecionada();
        this.recarregar();
      },
      error: () => this.erro.set('Não foi possível excluir a perícia.')
    });
  }


  carregarQuesitos(): void {
    const atual = this.periciaAtual();
    if (!atual) {
      this.quesitos.set([]);
      return;
    }

    this.carregandoQuesitos.set(true);
    this.erroQuesitos.set('');
    this.mensagemQuesitos.set('');

    this.service.listarQuesitos(atual.id).subscribe({
      next: (dados) => {
        this.quesitos.set(dados);
        this.carregandoQuesitos.set(false);
      },
      error: () => {
        this.erroQuesitos.set('Não foi possível carregar os quesitos desta perícia.');
        this.carregandoQuesitos.set(false);
      }
    });
  }

  novoQuesito(): void {
    this.quesitoEditandoId = null;
    this.quesitoForm = this.quesitoVazio();
    this.quesitoFormVisivel = true;
  }

  editarQuesito(q: Quesito): void {
    this.quesitoEditandoId = q.id;
    this.quesitoFormVisivel = true;
    this.quesitoForm = {
      codigo: q.codigo || '',
      numero: q.numero || 0,
      origem: q.origem || 'Juízo',
      texto: q.texto || '',
      status: q.status || 'Pendente',
      resposta_resumo: q.resposta_resumo || ''
    };
  }

  salvarQuesito(): void {
    const atual = this.periciaAtual();
    if (!atual) return;

    this.erroQuesitos.set('');
    this.mensagemQuesitos.set('');

    if (!this.quesitoForm.texto.trim()) {
      this.erroQuesitos.set('Informe o texto do quesito.');
      return;
    }

    const payload: QuesitoPayload = { ...this.quesitoForm };

    if (this.quesitoEditandoId) {
      this.service.atualizarQuesito(this.quesitoEditandoId, payload).subscribe({
        next: () => {
          this.mensagemQuesitos.set('Quesito atualizado com sucesso.');
          this.limparFormularioQuesito();
          this.carregarQuesitos();
        },
        error: () => this.erroQuesitos.set('Não foi possível atualizar o quesito.')
      });
      return;
    }

    const payloadQuesito: QuesitoPayload = { ...payload, codigo: '', numero: 0 };
    this.service.criarQuesito(atual.id, payloadQuesito).subscribe({
      next: () => {
        this.mensagemQuesitos.set('Quesito cadastrado com sucesso.');
        this.limparFormularioQuesito();
        this.carregarQuesitos();
      },
      error: () => this.erroQuesitos.set('Não foi possível cadastrar o quesito.')
    });
  }

  excluirQuesito(q: Quesito): void {
    if (!confirm(`Excluir o quesito nº ${q.numero}?`)) return;

    this.service.excluirQuesito(q.id).subscribe({
      next: () => {
        this.mensagemQuesitos.set('Quesito excluído com sucesso.');
        if (this.quesitoEditandoId === q.id) this.limparFormularioQuesito();
        this.carregarQuesitos();
      },
      error: () => this.erroQuesitos.set('Não foi possível excluir o quesito.')
    });
  }

  limparFormularioQuesito(): void {
    this.quesitoEditandoId = null;
    this.quesitoForm = this.quesitoVazio();
    this.quesitoFormVisivel = false;
  }


  carregarProfissionais(): void {
    this.service.listarProfissionais().subscribe({
      next: dados => this.profissionais.set(dados),
      error: () => this.erro.set('Não foi possível carregar profissionais.')
    });
  }

  carregarVinculosProfissionais(): void {
    const atual = this.periciaAtual();
    if (!atual) {
      this.vinculosProfissionais.set([]);
      return;
    }

    this.service.listarVinculosProfissionais(atual.id).subscribe({
      next: dados => {
        this.vinculosProfissionais.set(dados);
        if (this.peritoPrincipalVinculado() && this.vinculoForm.papel === 'Perito') {
          this.vinculoForm.papel = 'Assistente Técnico';
        }
      },
      error: () => this.erro.set('Não foi possível carregar equipe da perícia.')
    });
  }

  peritoPrincipalVinculado(): VinculoProfissional | null {
    return this.vinculosProfissionais().find(v => (v.papel || '').toLowerCase() === 'perito') || null;
  }

  papelEquipeDesabilitado(papel: string): boolean {
    return (papel || '').toLowerCase() === 'perito' && !!this.peritoPrincipalVinculado();
  }

  novoVinculoProfissional(): void {
    this.vinculoForm = { profissional_id: 0, papel: this.peritoPrincipalVinculado() ? 'Assistente Técnico' : 'Perito', observacoes: '' };
    this.vinculoFormVisivel = true;
  }

  cancelarVinculoProfissional(): void {
    this.vinculoForm = { profissional_id: 0, papel: this.peritoPrincipalVinculado() ? 'Assistente Técnico' : 'Perito', observacoes: '' };
    this.vinculoFormVisivel = false;
  }

  vincularProfissional(): void {
    const atual = this.periciaAtual();
    if ((this.vinculoForm.papel || '').toLowerCase() === 'perito' && this.peritoPrincipalVinculado()) {
      this.erro.set('Esta perícia já possui um perito vinculado. Remova o perito atual antes de cadastrar outro.');
      return;
    }
    if (!atual || !this.vinculoForm.profissional_id) {
      this.erro.set('Selecione um profissional para vincular.');
      return;
    }

    this.service.criarVinculoProfissional(atual.id, this.vinculoForm).subscribe({
      next: () => {
        this.mensagem.set('Profissional vinculado à perícia.');
        this.vinculoForm = { profissional_id: 0, papel: 'Perito', observacoes: '' };
        this.vinculoFormVisivel = false;
        this.carregarVinculosProfissionais();
      },
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível vincular profissional.')
    });
  }

  removerVinculoProfissional(vinculo: VinculoProfissional): void {
    if (!confirm(`Remover ${vinculo.nome} desta perícia?`)) return;

    this.service.excluirVinculoProfissional(vinculo.id).subscribe({
      next: () => {
        this.mensagem.set('Vínculo removido.');
        this.carregarVinculosProfissionais();
      },
      error: () => this.erro.set('Não foi possível remover vínculo.')
    });
  }

  carregarTarefas(): void {
    const atual = this.periciaAtual();
    if (!atual) {
      this.tarefas.set([]);
      return;
    }

    this.service.listarTarefasPericia(atual.id).subscribe({
      next: dados => this.tarefas.set(dados),
      error: () => this.erro.set('Não foi possível carregar calendário/tarefas.')
    });
  }

  novaTarefa(): void {
    this.tarefaEditandoId = null;
    this.tarefaForm = this.tarefaVazia();
  }

  editarTarefa(t: TarefaPericia): void {
    this.tarefaEditandoId = t.id;
    this.tarefaForm = {
      fase: t.fase || 'Planejamento',
      titulo: t.titulo || '',
      responsavel: t.responsavel || '',
      data_prevista: t.data_prevista || '',
      status: t.status || 'Pendente',
      pendencia: t.pendencia || ''
    };
  }

  salvarTarefa(): void {
    const atual = this.periciaAtual();
    if (!atual) return;

    if (!this.tarefaForm.titulo.trim()) {
      this.erro.set('Informe o título da tarefa.');
      return;
    }

    if (this.tarefaEditandoId) {
      this.service.atualizarTarefa(this.tarefaEditandoId, this.tarefaForm).subscribe({
        next: () => {
          this.mensagem.set('Tarefa atualizada.');
          this.novaTarefa();
          this.carregarTarefas();
        },
        error: () => this.erro.set('Não foi possível atualizar tarefa.')
      });
      return;
    }

    this.service.criarTarefa(atual.id, this.tarefaForm).subscribe({
      next: () => {
        this.mensagem.set('Tarefa cadastrada.');
        this.novaTarefa();
        this.carregarTarefas();
      },
      error: () => this.erro.set('Não foi possível cadastrar tarefa.')
    });
  }

  excluirTarefa(t: TarefaPericia): void {
    if (!confirm(`Excluir tarefa ${t.titulo}?`)) return;

    this.service.excluirTarefa(t.id).subscribe({
      next: () => {
        this.mensagem.set('Tarefa excluída.');
        this.carregarTarefas();
      },
      error: () => this.erro.set('Não foi possível excluir tarefa.')
    });
  }

  tarefasPorData(): { data: string; itens: TarefaPericia[] }[] {
    const grupos = new Map<string, TarefaPericia[]>();
    for (const tarefa of this.tarefas()) {
      const data = tarefa.data_prevista || 'Sem data';
      grupos.set(data, [...(grupos.get(data) || []), tarefa]);
    }
    return Array.from(grupos.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([data, itens]) => ({ data, itens }));
  }


  carregarPlanejamento(): void {
    const atual = this.periciaAtual();
    if (!atual) return;

    this.service.obterPlanejamento(atual.id).subscribe({
      next: dados => {
        this.planejamentoForm = dados ? {
          objeto_pericia: dados.objeto_pericia || '',
          escopo: dados.escopo || '',
          limites: dados.limites || '',
          criterios_tecnicos: dados.criterios_tecnicos || '',
          documentos_necessarios: dados.documentos_necessarios || '',
          riscos_limitacoes: dados.riscos_limitacoes || '',
          cronograma: dados.cronograma || '',
          equipe_apoio: dados.equipe_apoio || '',
          produtos_finais: dados.produtos_finais || '',
          status: dados.status || 'Em elaboração',
          data_conclusao: dados.data_conclusao || ''
        } : this.planejamentoVazio();
      },
      error: () => this.erro.set('Não foi possível carregar o planejamento.')
    });
  }

  salvarPlanejamento(): void {
    const atual = this.periciaAtual();
    if (!atual) return;

    const payloadPlanejamento = { ...this.planejamentoForm, documentos_necessarios: '', cronograma: '' };
    this.service.salvarPlanejamento(atual.id, payloadPlanejamento).subscribe({
      next: () => this.mensagem.set('Planejamento salvo com sucesso.'),
      error: () => this.erro.set('Não foi possível salvar o planejamento.')
    });
  }



  honorariosVazio(): HonorarioEstimativaPayload {
    return {
      tipo_atuacao: 'Perito do Juízo',
      modelo_estimativa: 'Por blocos de trabalho',
      valor_acao: 0,
      percentual_valor_acao: 0,
      valor_hora_padrao: 250,
      valor_fixo: 0,
      despesas_estimadas: 0,
      complexidade: 'Média',
      prazo_estimado: '',
      observacoes: '',
      status_honorarios: 'Em elaboração',
      valor_proposto: 0,
      valor_fixado_juiz: 0,
      data_proposta: '',
      data_decisao: '',
      observacoes_decisao: '',
      blocos: this.blocosPadraoHonorarios()
    };
  }

  blocosPadraoHonorarios(): HonorarioBlocoPayload[] {
    return [
      { bloco: 'Estudo inicial', descricao: 'Análise do processo, objeto, decisão e quesitos.', horas_estimadas: 2, valor_hora: 250, complexidade: 'Média', observacao: '' },
      { bloco: 'Planejamento técnico', descricao: 'Definição de escopo, metodologia, documentos e cronograma técnico.', horas_estimadas: 2, valor_hora: 250, complexidade: 'Média', observacao: '' },
      { bloco: 'Análise documental', descricao: 'Conferência dos documentos, bases, contratos, extratos e planilhas.', horas_estimadas: 6, valor_hora: 250, complexidade: 'Média', observacao: '' },
      { bloco: 'Cálculos e rotinas analíticas', descricao: 'Atualização monetária, juros, conciliações, duplicidades e simulações.', horas_estimadas: 6, valor_hora: 250, complexidade: 'Média', observacao: '' },
      { bloco: 'Diligências e esclarecimentos', descricao: 'Solicitação, acompanhamento e análise de complementações documentais.', horas_estimadas: 2, valor_hora: 250, complexidade: 'Média', observacao: '' },
      { bloco: 'Elaboração do laudo/parecer', descricao: 'Redação técnica, respostas aos quesitos, anexos e conclusão.', horas_estimadas: 8, valor_hora: 250, complexidade: 'Média', observacao: '' },
      { bloco: 'Revisão final', descricao: 'Conferência final, consistência técnica, formatação e fechamento.', horas_estimadas: 2, valor_hora: 250, complexidade: 'Média', observacao: '' }
    ];
  }

  carregarHonorarios(): void {
    const atual = this.periciaAtual();
    if (!atual) {
      this.honorariosEstimativa.set(null);
      this.honorariosForm = this.honorariosVazio();
      return;
    }
    this.service.obterHonorarios(atual.id).subscribe({
      next: (dados) => {
        if (dados) {
          this.honorariosEstimativa.set(dados);
          this.honorariosForm = this.estimativaParaForm(dados);
          if (!this.abaPermitida(this.abaAtiva())) this.abaAtiva.set(this.primeiraAbaPermitida());
        } else {
          this.honorariosEstimativa.set(null);
          this.honorariosForm = this.honorariosVazio();
        }
      },
      error: () => this.erro.set('Não foi possível carregar honorários.')
    });
  }

  estimativaParaForm(dados: HonorarioEstimativa): HonorarioEstimativaPayload {
    return {
      tipo_atuacao: dados.tipo_atuacao || 'Perito do Juízo',
      modelo_estimativa: dados.modelo_estimativa || 'Por blocos de trabalho',
      valor_acao: Number(dados.valor_acao || 0),
      percentual_valor_acao: Number(dados.percentual_valor_acao || 0),
      valor_hora_padrao: Number(dados.valor_hora_padrao || 250),
      valor_fixo: Number(dados.valor_fixo || 0),
      despesas_estimadas: Number(dados.despesas_estimadas || 0),
      complexidade: dados.complexidade || 'Média',
      prazo_estimado: dados.prazo_estimado || '',
      observacoes: dados.observacoes || '',
      status_honorarios: dados.status_honorarios || 'Em elaboração',
      valor_proposto: Number(dados.valor_proposto || dados.total_final || 0),
      valor_fixado_juiz: Number(dados.valor_fixado_juiz || 0),
      data_proposta: dados.data_proposta || '',
      data_decisao: dados.data_decisao || '',
      observacoes_decisao: dados.observacoes_decisao || '',
      blocos: (dados.blocos || []).map(b => ({
        bloco: b.bloco || '',
        descricao: b.descricao || '',
        horas_estimadas: Number(b.horas_estimadas || 0),
        valor_hora: Number(b.valor_hora || 0),
        complexidade: b.complexidade || 'Média',
        observacao: b.observacao || ''
      }))
    };
  }

  adicionarBlocoHonorarios(): void {
    this.honorariosForm.blocos.push({ bloco: 'Novo bloco', descricao: '', horas_estimadas: 0, valor_hora: Number(this.honorariosForm.valor_hora_padrao || 0), complexidade: 'Média', observacao: '' });
  }

  removerBlocoHonorarios(index: number): void {
    this.honorariosForm.blocos.splice(index, 1);
  }

  aplicarValorHoraPadraoNosBlocos(): void {
    const valor = Number(this.honorariosForm.valor_hora_padrao || 0);
    this.honorariosForm.blocos = this.honorariosForm.blocos.map(b => ({ ...b, valor_hora: valor }));
  }

  restaurarBlocosPadraoHonorarios(): void {
    const valor = Number(this.honorariosForm.valor_hora_padrao || 250);
    this.honorariosForm.blocos = this.blocosPadraoHonorarios().map(b => ({ ...b, valor_hora: valor }));
  }

  valorBlocoHonorarios(bloco: HonorarioBlocoPayload): number {
    return Number(bloco.horas_estimadas || 0) * Number(bloco.valor_hora || 0);
  }

  totalBlocosHonorariosForm(): number {
    return this.honorariosForm.blocos.reduce((soma, b) => soma + this.valorBlocoHonorarios(b), 0);
  }

  totalPercentualHonorariosForm(): number {
    return Number(this.honorariosForm.valor_acao || 0) * (Number(this.honorariosForm.percentual_valor_acao || 0) / 100);
  }

  totalFinalHonorariosForm(): number {
    const modelo = this.honorariosForm.modelo_estimativa;
    const blocos = this.totalBlocosHonorariosForm();
    const percentual = this.totalPercentualHonorariosForm();
    const fixo = Number(this.honorariosForm.valor_fixo || 0);
    const despesas = Number(this.honorariosForm.despesas_estimadas || 0);
    if (modelo === 'Percentual sobre valor da ação') return percentual + despesas;
    if (modelo === 'Valor fixo') return fixo + despesas;
    if (modelo === 'Modelo misto') return fixo + blocos + percentual + despesas;
    return blocos + despesas;
  }

  criterioHonorariosV914(): string {
    const modelo = this.honorariosForm.modelo_estimativa;
    if (modelo === 'Percentual sobre valor da ação') return 'Percentual sobre valor da ação + despesas';
    if (modelo === 'Valor fixo') return 'Valor fixo + despesas';
    if (modelo === 'Modelo misto') return 'Valor fixo + blocos + percentual + despesas';
    return 'Blocos de trabalho + despesas';
  }

  valorConsideradoHonorarios(): number {
    return Number(this.honorariosForm.valor_fixado_juiz || 0) > 0 ? Number(this.honorariosForm.valor_fixado_juiz || 0) : this.totalFinalHonorariosForm();
  }

  private prepararHonorariosJudicialParaSalvar(): void {
    this.honorariosForm = {
      ...this.honorariosForm,
      valor_proposto: this.totalFinalHonorariosForm()
    };
  }

  salvarHonorariosV914(): void {
    const atual = this.periciaAtual();
    if (!atual) return;
    this.prepararHonorariosJudicialParaSalvar();
    this.salvandoHonorarios = true;
    this.service.salvarHonorarios(atual.id, this.honorariosForm).subscribe({
      next: (dados) => {
        this.honorariosEstimativa.set(dados);
        this.honorariosForm = this.estimativaParaForm(dados);
        this.mensagem.set('Salvo.');
        if (!this.abaPermitida(this.abaAtiva())) this.abaAtiva.set('Honorários');
        this.salvandoHonorarios = false;
      },
      error: () => {
        this.erro.set('Erro ao salvar honorários.');
        this.salvandoHonorarios = false;
      }
    });
  }

  tipoAtuacaoSistemaHonorarios(): string {
    const destino = this.destinoPreviewHonorarios || this.destinoExportacaoHonorariosSelecionado || 'juizo';
    if (destino === 'assistente') return 'Assistente técnico da parte';
    if (destino === 'extrajudicial') return 'Consultoria extrajudicial';
    return 'Perito do Juízo';
  }

  private prepararHonorariosParaDestino(destino: string): void {
    this.destinoExportacaoHonorariosSelecionado = destino || 'juizo';
    this.destinoPreviewHonorarios = destino || 'juizo';
    this.honorariosForm = {
      ...this.honorariosForm,
      tipo_atuacao: this.tipoAtuacaoSistemaHonorarios()
    };
  }

  private tituloPreviewHonorarios(destino: string): string {
    if (destino === 'assistente') return 'Prévia — Modelo para assistente técnico / advogado da parte';
    if (destino === 'extrajudicial') return 'Prévia — Modelo para cliente extrajudicial';
    return 'Prévia — Modelo para o Juízo';
  }

  exportarHonorariosSelecionado(): void {
    this.exportarHonorariosArquivo(
      this.destinoPreviewHonorarios || this.destinoExportacaoHonorariosSelecionado || 'juizo',
      this.formatoExportacaoHonorariosSelecionado || 'pdf'
    );
  }

  exportarHonorarios(destino: string): void {
    this.exportarHonorariosArquivo(destino, 'html');
  }

  exportarHonorariosArquivo(destino: string, formato: string): void {
    const atual = this.periciaAtual();
    if (!atual) return;

    this.prepararHonorariosParaDestino(destino);
    this.prepararHonorariosJudicialParaSalvar();
    this.salvandoHonorarios = true;
    this.service.salvarHonorarios(atual.id, this.honorariosForm).subscribe({
      next: (dados) => {
        this.honorariosEstimativa.set(dados);
        this.honorariosForm = this.estimativaParaForm(dados);
        this.prepararHonorariosParaDestino(destino);
        this.salvandoHonorarios = false;
        this.service.exportarHonorariosArquivo(atual.id, destino, formato);
      },
      error: () => {
        this.erro.set('Não foi possível salvar os honorários antes da exportação.');
        this.salvandoHonorarios = false;
      }
    });
  }

  visualizarPreviewHonorarios(destino: string = 'juizo'): void {
    const atual = this.periciaAtual();
    if (!atual) return;
    this.prepararHonorariosParaDestino(destino);
    this.prepararHonorariosJudicialParaSalvar();
    this.previewHonorariosTitulo = this.tituloPreviewHonorarios(destino);

    this.salvandoHonorarios = true;
    this.service.salvarHonorarios(atual.id, this.honorariosForm).subscribe({
      next: (dados) => {
        this.honorariosEstimativa.set(dados);
        this.honorariosForm = this.estimativaParaForm(dados);
        this.prepararHonorariosParaDestino(destino);
        this.service.previewHonorariosHtml(atual.id, destino).subscribe({
          next: (html) => {
            this.previewHonorariosHtml = html;
            this.visualizandoPreviewHonorarios = true;
            this.salvandoHonorarios = false;
          },
          error: () => {
            this.erro.set('Não foi possível gerar o preview da proposta.');
            this.salvandoHonorarios = false;
          }
        });
      },
      error: () => {
        this.erro.set('Não foi possível salvar os honorários antes do preview.');
        this.salvandoHonorarios = false;
      }
    });
  }

  fecharPreviewHonorarios(): void {
    this.visualizandoPreviewHonorarios = false;
    this.previewHonorariosHtml = '';
  }

  honorarioVazio(): ItemHonorario {
    return { id: 0, etapa: 'Pesquisa documental', atividade: '', horas: 0, valorHora: this.valorHoraBaseHonorarios, despesas: 0, status: 'Previsto', observacoes: '', origem: 'Manual', codigoTarefa: '' };
  }

  esclarecimentoVazio(): ItemEsclarecimento {
    return { id: 0, dataPedido: '', origem: 'Juízo', pontoQuestionado: '', resposta: '', prazo: '', status: 'Não solicitado', documentoVinculado: '' };
  }

  private horasEstimadasPrograma(programa: ProgramaTrabalho): number {
    const texto = `${programa.etapa || ''} ${programa.tarefa || ''} ${programa.procedimento || ''}`.toLowerCase();
    if (texto.includes('laudo')) return 8;
    if (texto.includes('cálculo') || texto.includes('calculo') || texto.includes('execuç') || texto.includes('analítica') || texto.includes('analitica')) return 6;
    if (texto.includes('dilig')) return 3;
    if (texto.includes('document')) return 3;
    if (texto.includes('quesito')) return 2;
    if (texto.includes('planejamento') || texto.includes('escopo')) return 2;
    return 2;
  }

  private etapaHonorarioPorPrograma(programa: ProgramaTrabalho): string {
    const etapa = (programa.etapa || '').toLowerCase();
    const tarefa = (programa.tarefa || '').toLowerCase();
    if (etapa.includes('laudo') || tarefa.includes('laudo')) return 'Elaboração do laudo';
    if (etapa.includes('cálculo') || etapa.includes('calculo') || etapa.includes('execuç')) return 'Planilhas e cálculos';
    if (etapa.includes('dilig')) return 'Termos de diligência';
    if (etapa.includes('document')) return 'Pesquisa documental';
    if (etapa.includes('análise') || etapa.includes('analise') || etapa.includes('planejamento')) return 'Análise inicial';
    return 'Exame contábil/fiscal/societário';
  }

  sincronizarHonorariosComTarefas(): void {
    const programas = this.programasOrdenados();
    if (!programas.length) {
      this.erro.set('Cadastre ou carregue as tarefas em Programas/Tarefas antes de gerar honorários.');
      return;
    }

    const existentes = this.honorarios();
    const novos: ItemHonorario[] = [];

    for (const p of programas) {
      const codigo = p.codigo || ('PRG-' + String(p.ordem || 0).padStart(3, '0'));
      const jaExiste = existentes.some(h => h.codigoTarefa === codigo || h.atividade === p.tarefa);
      if (jaExiste) continue;

      novos.push({
        id: Date.now() + novos.length,
        etapa: this.etapaHonorarioPorPrograma(p),
        atividade: p.tarefa || 'Tarefa sem descrição',
        horas: this.horasEstimadasPrograma(p),
        valorHora: this.valorHoraBaseHonorarios,
        despesas: 0,
        status: 'Previsto',
        observacoes: `Gerado a partir da tarefa ${codigo}. Ajuste as horas conforme complexidade e valor da ação.`,
        origem: 'Tarefa',
        codigoTarefa: codigo
      });
    }

    if (!novos.length) {
      this.mensagem.set('Os honorários já estão sincronizados com as tarefas cadastradas.');
      return;
    }

    this.honorarios.update(lista => [...lista, ...novos]);
    this.mensagem.set(novos.length + ' item(ns) de honorários gerado(s) a partir das tarefas.');
  }

  aplicarValorHoraBaseHonorarios(): void {
    const valor = Number(this.valorHoraBaseHonorarios || 0);
    this.honorarios.update(lista => lista.map(h => ({ ...h, valorHora: valor })));
    this.mensagem.set('Valor/hora base aplicado aos itens de honorários.');
  }

  adicionarHonorariosPadrao(): void {
    this.sincronizarHonorariosComTarefas();
  }

  salvarHonorario(): void {
    if (!this.honorarioForm.atividade.trim()) {
      this.erro.set('Informe a atividade de honorários.');
      return;
    }
    const item = { ...this.honorarioForm, id: this.honorarioEditandoId || Date.now() };
    if (this.honorarioEditandoId) {
      this.honorarios.update(lista => lista.map(h => h.id === this.honorarioEditandoId ? item : h));
      this.mensagem.set('Item de honorários atualizado.');
    } else {
      this.honorarios.update(lista => [...lista, item]);
      this.mensagem.set('Item de honorários incluído.');
    }
    this.novoHonorario();
  }

  novoHonorario(): void {
    this.honorarioEditandoId = null;
    this.honorarioForm = this.honorarioVazio();
  }

  editarHonorario(item: ItemHonorario): void {
    this.honorarioEditandoId = item.id;
    this.honorarioForm = { ...item };
  }

  excluirHonorario(item: ItemHonorario): void {
    if (!confirm(`Excluir o item de honorários: ${item.atividade}?`)) return;
    this.honorarios.update(lista => lista.filter(h => h.id !== item.id));
    this.mensagem.set('Item de honorários excluído.');
  }

  totalHorasHonorarios(): number {
    return this.honorarios().reduce((s, h) => s + Number(h.horas || 0), 0);
  }

  subtotalHonorariosSemComplexidade(): number {
    return this.honorarios().reduce((s, h) => s + (Number(h.horas || 0) * Number(h.valorHora || 0)) + Number(h.despesas || 0), 0);
  }

  totalValorHonorarios(): number {
    return this.subtotalHonorariosSemComplexidade() * Number(this.fatorComplexidadeHonorarios || 1);
  }

  percentualHonorariosSobreValorAcao(): number {
    const valorAcao = Number(this.valorAcaoHonorarios || 0);
    if (!valorAcao) return 0;
    return (this.totalValorHonorarios() / valorAcao) * 100;
  }

  valorTarefaHonorarios(programa: ProgramaTrabalho | ProgramaTrabalhoPayload): number {
    return (Number(programa.horas_estimadas || 0) * Number(programa.valor_hora || 0)) + Number(programa.despesas_honorarios || 0);
  }

  totalHorasProgramasHonorarios(): number {
    return this.programas().reduce((s, p) => s + Number(p.horas_estimadas || 0), 0);
  }

  subtotalProgramasHonorarios(): number {
    return this.programas().reduce((s, p) => s + this.valorTarefaHonorarios(p), 0);
  }

  totalDespesasProgramasHonorarios(): number {
    return this.programas().reduce((s, p) => s + Number(p.despesas_honorarios || 0), 0);
  }

  totalProgramasHonorarios(): number {
    return this.subtotalProgramasHonorarios();
  }

  totalPercentualValorAcaoHonorarios(): number {
    const valorAcao = Number(this.valorAcaoHonorarios || 0);
    const percentual = Number(this.percentualValorAcaoHonorarios || 0);
    if (!valorAcao || !percentual) return 0;
    return (valorAcao * percentual) / 100;
  }

  totalFinalHonorarios(): number {
    const porPercentual = this.totalPercentualValorAcaoHonorarios();
    return porPercentual > 0 ? porPercentual : this.totalProgramasHonorarios();
  }

  criterioHonorarios(): string {
    return this.totalPercentualValorAcaoHonorarios() > 0 ? 'Percentual sobre o valor da ação' : 'Horas das tarefas + despesas';
  }

  percentualProgramasHonorariosSobreValorAcao(): number {
    const valorAcao = Number(this.valorAcaoHonorarios || 0);
    if (!valorAcao) return 0;
    return (this.totalFinalHonorarios() / valorAcao) * 100;
  }

  aplicarValorHoraBaseNasTarefas(): void {
    const valor = Number(this.valorHoraBaseHonorarios || 0);
    const atualizacoes = this.programas().map(p => this.service.atualizarProgramaTrabalho(p.id, {
      codigo: p.codigo || '',
      ordem: p.ordem || 0,
      etapa: p.etapa || '',
      tarefa: p.tarefa || '',
      descricao: p.descricao || '',
      procedimento: p.procedimento || '',
      documento_necessario: p.documento_necessario || '',
      responsavel: p.responsavel || '',
      data_prevista: p.data_prevista || '',
      data_conclusao: p.data_conclusao || '',
      status: p.status || 'Pendente',
      resultado: p.resultado || '',
      observacoes: p.observacoes || '',
      horas_estimadas: Number(p.horas_estimadas || 0),
      valor_hora: valor,
      despesas_honorarios: Number(p.despesas_honorarios || 0)
    }));

    if (!atualizacoes.length) {
      this.mensagem.set('Nenhuma tarefa cadastrada para aplicar valor/hora.');
      return;
    }

    Promise.all(atualizacoes.map(obs => firstValueFrom(obs)))
      .then(() => {
        this.mensagem.set('Valor/hora aplicado nas tarefas cadastradas.');
        this.carregarProgramas();
      })
      .catch(() => this.erro.set('Não foi possível aplicar valor/hora nas tarefas.'));
  }

  salvarEsclarecimento(): void {
    if (!this.esclarecimentoForm.pontoQuestionado.trim()) {
      this.erro.set('Informe o ponto questionado no esclarecimento.');
      return;
    }
    const item = { ...this.esclarecimentoForm, id: this.esclarecimentoEditandoId || Date.now() };
    if (this.esclarecimentoEditandoId) {
      this.esclarecimentos.update(lista => lista.map(e => e.id === this.esclarecimentoEditandoId ? item : e));
      this.mensagem.set('Esclarecimento atualizado.');
    } else {
      this.esclarecimentos.update(lista => [...lista, item]);
      this.mensagem.set('Esclarecimento registrado.');
    }
    this.novoEsclarecimento();
  }

  novoEsclarecimento(): void {
    this.esclarecimentoEditandoId = null;
    this.esclarecimentoForm = this.esclarecimentoVazio();
  }

  editarEsclarecimento(item: ItemEsclarecimento): void {
    this.esclarecimentoEditandoId = item.id;
    this.esclarecimentoForm = { ...item };
  }

  excluirEsclarecimento(item: ItemEsclarecimento): void {
    if (!confirm('Excluir este esclarecimento?')) return;
    this.esclarecimentos.update(lista => lista.filter(e => e.id !== item.id));
    this.mensagem.set('Esclarecimento excluído.');
  }

  esclarecimentosPendentes(): number {
    return this.esclarecimentos().filter(e => ['Solicitado', 'Em elaboração'].includes(e.status || '')).length;
  }

  carregarProgramas(): void {
    const atual = this.periciaAtual();
    if (!atual) {
      this.programas.set([]);
      return;
    }

    this.service.listarProgramasTrabalhoPericia(atual.id).subscribe({
      next: dados => {
        this.programas.set(dados);
        this.garantirTarefasComunsPadrao();
      },
      error: () => this.erro.set('Não foi possível carregar programas de trabalho.')
    });
  }


  tipoPericiaSelecionado(): string {
    return this.formulario.tipo_pericia || this.periciaAtual()?.tipo_pericia || 'Perícia Contábil';
  }

  tipoPericiaNormalizado(): string {
    const tipo = this.tipoPericiaSelecionado();
    if (tipo === 'Perícia na Recuperação Judicial e Falência de Empresas') {
      return 'Perícia na Recuperação Judicial e Falência';
    }
    return tipo;
  }

  tarefasPadraoComuns(): TarefaPadraoTipo[] {
    return this.tarefasComunsPadrao;
  }

  tarefasPadraoEspecificas(): TarefaPadraoTipo[] {
    const tipo = this.tipoPericiaNormalizado();
    return this.tarefasPadraoPorTipo.filter(t => t.tipoPericia === tipo);
  }

  tarefasPadraoDaPericia(): TarefaPadraoTipo[] {
    return [...this.tarefasPadraoComuns(), ...this.tarefasPadraoEspecificas()];
  }

  tarefaPadraoExiste(tarefa: TarefaPadraoTipo): boolean {
    const alvo = this.normalizarTexto(tarefa.tarefa);
    return this.programas().some(p => this.normalizarTexto(p.tarefa || '') === alvo);
  }

  tarefasPadraoPendentes(): TarefaPadraoTipo[] {
    return this.tarefasPadraoDaPericia().filter(t => !this.tarefaPadraoExiste(t));
  }

  tarefasComunsPendentes(): TarefaPadraoTipo[] {
    return this.tarefasPadraoComuns().filter(t => !this.tarefaPadraoExiste(t));
  }

  tarefasEspecificasPendentes(): TarefaPadraoTipo[] {
    return this.tarefasPadraoEspecificas().filter(t => !this.tarefaPadraoExiste(t));
  }

  quantidadeTarefasPendentesPorCategoria(categoria: 'Comum' | 'Específica'): number {
    return this.tarefasPadraoDaPericia().filter(t => t.categoria === categoria && !this.tarefaPadraoExiste(t)).length;
  }

  dataPrevistaPorDias(prazoDias: number): string {
    const base = new Date();
    base.setDate(base.getDate() + prazoDias);
    return base.toISOString().substring(0, 10);
  }

  codigoPreviewPrograma(): string {
    const maior = this.programas().reduce((m, p) => Math.max(m, p.ordem || 0), 0);
    return 'PRG-' + String(maior + 1).padStart(3, '0');
  }

  cronogramaProgramasPorData(): { data: string; itens: ProgramaTrabalho[] }[] {
    const grupos = new Map<string, ProgramaTrabalho[]>();
    for (const programa of this.programas()) {
      const data = programa.data_prevista || 'Sem data prevista';
      grupos.set(data, [...(grupos.get(data) || []), programa]);
    }
    return Array.from(grupos.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([data, itens]) => ({ data, itens }));
  }

  programasOrdenados(): ProgramaTrabalho[] {
    return [...this.programas()].sort((a, b) => {
      const ordemA = a.ordem || 0;
      const ordemB = b.ordem || 0;
      if (ordemA !== ordemB) return ordemA - ordemB;
      return (a.tarefa || '').localeCompare(b.tarefa || '');
    });
  }

  faseCronograma(programa: ProgramaTrabalho): string {
    const etapa = (programa.etapa || '').toLowerCase();
    if (['planejamento', 'análise inicial', 'analise inicial', 'documentos', 'quesitos'].includes(etapa)) return 'Preparação';
    if (['cálculos', 'calculos', 'diligências', 'diligencias', 'execuções', 'execuções', 'achados', 'evidências', 'evidencias'].includes(etapa)) return 'Desenvolvimento';
    if (['laudo', 'checklist'].includes(etapa)) return 'Conclusão';
    return 'Operacional';
  }

  private alinharDataParaSemana(data: Date): Date {
    const copia = new Date(data.getFullYear(), data.getMonth(), data.getDate());
    return copia;
  }

  private dataValida(valor?: string | null): Date | null {
    if (!valor) return null;
    const partes = valor.split('-').map(Number);
    if (partes.length !== 3 || partes.some(Number.isNaN)) return null;
    const [ano, mes, dia] = partes;
    return new Date(ano, mes - 1, dia);
  }

  private obterPrazoDiasDoPrograma(programa: ProgramaTrabalho): number {
    const obs = programa.observacoes || '';
    const match = obs.match(/Prazo sugerido:\s*(\d+)/i);
    return match ? Math.max(1, Number(match[1])) : 5;
  }

  cronogramaColunas(): { indice: number; rotulo: string }[] {
    const programas = this.programasOrdenados();
    if (!programas.length) return [];

    const datas = programas
      .map(p => this.dataValida(p.data_prevista || ''))
      .filter((d): d is Date => d instanceof Date && !Number.isNaN(d.getTime()));

    const base = datas.length ? new Date(Math.min(...datas.map(d => d.getTime()))) : new Date();
    const inicio = this.alinharDataParaSemana(base);

    let maiorIndice = 0;
    for (const programa of programas) {
      const indice = this.indiceSemanaPrograma(programa, inicio);
      const duracao = this.duracaoSemanasPrograma(programa);
      maiorIndice = Math.max(maiorIndice, indice + duracao - 1);
    }

    const total = Math.max(8, Math.min(14, maiorIndice + 2));
    return Array.from({ length: total }, (_, indice) => {
      const data = new Date(inicio);
      data.setDate(inicio.getDate() + indice * 7);
      return { indice, rotulo: `${String(data.getDate()).padStart(2, '0')}/${String(data.getMonth() + 1).padStart(2, '0')}` };
    });
  }

  indiceSemanaPrograma(programa: ProgramaTrabalho, inicioBase?: Date): number {
    const base = inicioBase || (() => {
      const cols = this.cronogramaColunas();
      if (!cols.length) return new Date();
      const [dia, mes] = cols[0].rotulo.split('/').map(Number);
      return new Date(new Date().getFullYear(), mes - 1, dia);
    })();

    const data = this.dataValida(programa.data_prevista || '');
    if (data) {
      const diffDias = Math.floor((data.getTime() - base.getTime()) / 86400000);
      return Math.max(0, Math.floor(diffDias / 7));
    }

    const ordem = Math.max(0, (programa.ordem || 1) - 1);
    return Math.floor(ordem / 2);
  }

  duracaoSemanasPrograma(programa: ProgramaTrabalho): number {
    const dataInicio = this.dataValida(programa.data_prevista || '');
    const dataFim = this.dataValida(programa.data_conclusao || '');
    if (dataInicio && dataFim && dataFim.getTime() >= dataInicio.getTime()) {
      const diffDias = Math.floor((dataFim.getTime() - dataInicio.getTime()) / 86400000) + 1;
      return Math.max(1, Math.ceil(diffDias / 7));
    }
    return Math.max(1, Math.ceil(this.obterPrazoDiasDoPrograma(programa) / 7));
  }

  programaAtivoNaSemana(programa: ProgramaTrabalho, indiceSemana: number): boolean {
    const programas = this.programasOrdenados();
    const datas = programas
      .map(p => this.dataValida(p.data_prevista || ''))
      .filter((d): d is Date => d instanceof Date && !Number.isNaN(d.getTime()));
    const inicio = datas.length ? new Date(Math.min(...datas.map(d => d.getTime()))) : new Date();
    const inicioPrograma = this.indiceSemanaPrograma(programa, inicio);
    const fimPrograma = inicioPrograma + this.duracaoSemanasPrograma(programa) - 1;
    return indiceSemana >= inicioPrograma && indiceSemana <= fimPrograma;
  }

  classeBarraCronograma(status: string): string {
    const valor = (status || '').toLowerCase();
    if (valor.includes('concl')) return 'concluida';
    if (valor.includes('andamento')) return 'andamento';
    if (valor.includes('atras')) return 'atrasada';
    if (valor.includes('aguard')) return 'aguardando';
    return 'pendente';
  }

  dataInicialCronograma(programa: ProgramaTrabalho): string {
    const data = this.dataValida(programa.data_prevista || '');
    return data ? this.formatarDataCronograma(data) : '-';
  }

  dataFinalCronograma(programa: ProgramaTrabalho): string {
    const dataFim = this.obterDataFinalCronograma(programa);
    return dataFim ? this.formatarDataCronograma(dataFim) : '-';
  }

  obterDataFinalCronograma(programa: ProgramaTrabalho): Date | null {
    const dataFim = this.dataValida(programa.data_conclusao || '');
    if (dataFim) return dataFim;
    const dataInicio = this.dataValida(programa.data_prevista || '');
    if (!dataInicio) return null;
    const prazo = Math.max(1, this.obterPrazoDiasDoPrograma(programa));
    const estimada = new Date(dataInicio);
    estimada.setDate(estimada.getDate() + Math.max(0, prazo - 1));
    return estimada;
  }

  private formatarDataCronograma(data: Date): string {
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = String(data.getFullYear());
    return `${dia}/${mes}/${ano}`;
  }

  legendaStatusCronograma(): { classe: string; rotulo: string }[] {
    return [
      { classe: 'pendente', rotulo: 'Pendente' },
      { classe: 'andamento', rotulo: 'Em andamento' },
      { classe: 'aguardando', rotulo: 'Aguardando documento' },
      { classe: 'concluida', rotulo: 'Concluída' },
      { classe: 'atrasada', rotulo: 'Atrasada' }
    ];
  }

  private montarPayloadTarefaPadrao(tarefa: TarefaPadraoTipo): ProgramaTrabalhoPayload {
    return {
      codigo: '',
      ordem: 0,
      etapa: tarefa.etapa,
      tarefa: tarefa.tarefa,
      descricao: 'Tarefa ' + tarefa.categoria.toLowerCase() + ' para ' + this.tipoPericiaSelecionado() + '.',
      procedimento: tarefa.procedimento,
      documento_necessario: tarefa.documento_necessario,
      responsavel: tarefa.responsavel,
      data_prevista: this.dataPrevistaPorDias(tarefa.prazoDias),
      data_conclusao: '',
      status: 'Pendente',
      resultado: '',
      observacoes: 'Origem: ' + tarefa.categoria + '. Prazo sugerido: ' + tarefa.prazoDias + ' dia(s).',
      horas_estimadas: this.horasEstimadasPrograma({ etapa: tarefa.etapa, tarefa: tarefa.tarefa, procedimento: tarefa.procedimento } as ProgramaTrabalho),
      valor_hora: Number(this.valorHoraBaseHonorarios || 0),
      despesas_honorarios: 0
    };
  }

  private async adicionarListaTarefasPadrao(tarefas: TarefaPadraoTipo[], exibirMensagem: boolean): Promise<void> {
    const atual = this.periciaAtual();
    if (!atual || !tarefas.length) return;

    let adicionadas = 0;
    for (const tarefa of tarefas) {
      if (this.tarefaPadraoExiste(tarefa)) continue;
      const criado = await firstValueFrom(this.service.criarProgramaTrabalho(atual.id, this.montarPayloadTarefaPadrao(tarefa)));
      adicionadas++;
      this.programas.update(lista => [...lista, criado]);
    }

    if (adicionadas) {
      this.carregarProgramas();
      if (exibirMensagem) {
        this.mensagem.set(adicionadas + ' tarefa(s) específica(s) adicionada(s) ao cronograma.');
      }
    }
  }

  async garantirTarefasComunsPadrao(): Promise<void> {
    const atual = this.periciaAtual();
    if (!atual || this.adicionandoComunsPadrao) return;

    const comunsPendentes = this.tarefasComunsPendentes();
    if (!comunsPendentes.length) return;

    this.adicionandoComunsPadrao = true;
    try {
      await this.adicionarListaTarefasPadrao(comunsPendentes, false);
    } catch {
      this.erro.set('Não foi possível adicionar automaticamente as tarefas comuns padrão.');
    } finally {
      this.adicionandoComunsPadrao = false;
    }
  }

  adicionarTarefaPadrao(tarefa: TarefaPadraoTipo): void {
    const atual = this.periciaAtual();
    if (!atual) return;

    if (this.tarefaPadraoExiste(tarefa)) {
      this.mensagem.set('Tarefa padrão já consta no programa de trabalho.');
      return;
    }

    this.adicionarListaTarefasPadrao([tarefa], true)
      .catch(() => this.erro.set('Não foi possível adicionar tarefa padrão.'));
  }

  adicionarTarefasPadraoPendentes(): void {
    const pendentes = this.tarefasEspecificasPendentes();
    if (!pendentes.length) {
      this.mensagem.set('Todas as tarefas específicas deste tipo de perícia já foram adicionadas.');
      return;
    }

    this.adicionarListaTarefasPadrao(pendentes, true)
      .catch(() => this.erro.set('Uma ou mais tarefas específicas não puderam ser adicionadas.'));
  }

  novoPrograma(): void {
    this.programaEditandoId = null;
    this.programaForm = this.programaVazio();
    this.programaFormVisivel = true;
  }

  cancelarPrograma(): void {
    this.programaEditandoId = null;
    this.programaForm = this.programaVazio();
    this.programaFormVisivel = false;
  }

  editarPrograma(p: ProgramaTrabalho): void {
    this.programaEditandoId = p.id;
    this.programaFormVisivel = true;
    this.programaForm = {
      codigo: p.codigo || '',
      ordem: p.ordem || 0,
      etapa: p.etapa || '',
      tarefa: p.tarefa || '',
      descricao: p.descricao || '',
      procedimento: p.procedimento || '',
      documento_necessario: p.documento_necessario || '',
      responsavel: p.responsavel || '',
      data_prevista: p.data_prevista || '',
      data_conclusao: p.data_conclusao || '',
      status: p.status || 'Pendente',
      resultado: p.resultado || '',
      observacoes: p.observacoes || '',
      horas_estimadas: Number(p.horas_estimadas || 0),
      valor_hora: Number(p.valor_hora || this.valorHoraBaseHonorarios || 0),
      despesas_honorarios: Number(p.despesas_honorarios || 0)
    };
  }

  salvarPrograma(): void {
    const atual = this.periciaAtual();
    if (!atual) return;

    if (!this.programaForm.tarefa.trim()) {
      this.erro.set('Informe a tarefa do programa de trabalho.');
      return;
    }

    if (this.programaEditandoId) {
      this.service.atualizarProgramaTrabalho(this.programaEditandoId, this.programaForm).subscribe({
        next: () => {
          this.mensagem.set('Programa atualizado.');
          this.cancelarPrograma();
          this.carregarProgramas();
        },
        error: () => this.erro.set('Não foi possível atualizar o programa.')
      });
      return;
    }

    const payloadPrograma = { ...this.programaForm, codigo: '', ordem: 0 };
    this.service.criarProgramaTrabalho(atual.id, payloadPrograma).subscribe({
      next: () => {
        this.mensagem.set('Programa cadastrado.');
        this.cancelarPrograma();
        this.carregarProgramas();
      },
      error: () => this.erro.set('Não foi possível cadastrar o programa.')
    });
  }

  excluirPrograma(p: ProgramaTrabalho): void {
    if (!confirm(`Excluir a tarefa ${p.codigo || p.ordem} - ${p.tarefa}?`)) return;

    this.service.excluirProgramaTrabalho(p.id).subscribe({
      next: () => {
        this.mensagem.set('Programa excluído.');
        this.carregarProgramas();
      },
      error: () => this.erro.set('Não foi possível excluir o programa.')
    });
  }



  selecionarArquivoDocumento(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.arquivoDocumentoSelecionado = input.files?.[0] || null;
  }

  enviarArquivoDocumento(): void {
    const atual = this.periciaAtual();
    if (!atual || !this.arquivoDocumentoSelecionado) {
      this.erro.set('Selecione uma perícia e um arquivo.');
      return;
    }

    this.service.uploadDocumento(atual.id, this.arquivoDocumentoSelecionado).subscribe({
      next: (res) => {
        this.documentoForm.caminho_arquivo = res.caminho_arquivo || '';
        this.documentoForm.hash_sha256 = res.hash_sha256 || '';
        this.documentoForm.formato = res.formato || this.documentoForm.formato;
        this.mensagem.set(`Arquivo enviado. Hash e caminho foram preenchidos automaticamente.`);
      },
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível enviar o arquivo.')
    });
  }

  baixarDocumento(d: DocumentoPericia): void {
    this.service.baixarDocumento(d.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const nome = d.caminho_arquivo ? d.caminho_arquivo.split(/[\\\\/]/).pop() : `${d.codigo || 'documento'}.bin`;
        a.href = url;
        a.download = nome || 'documento.bin';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível baixar o documento.')
    });
  }

  carregarDocumentos(): void {
    const atual = this.periciaAtual();
    if (!atual) {
      this.documentosPericia.set([]);
      return;
    }

    this.service.listarDocumentosPericia(atual.id).subscribe({
      next: dados => this.documentosPericia.set(dados),
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível carregar documentos. Verifique se a rota aparece no /docs e se a tabela pericia.documentos existe.')
    });
  }


  documentosPadraoDaPericia(): DocumentoPadraoTipo[] {
    const tipo = this.formulario.tipo_pericia || this.periciaAtual()?.tipo_pericia || '';
    return this.documentosPadraoPorTipo.filter(d => d.tipoPericia === tipo);
  }

  documentoPadraoExiste(doc: DocumentoPadraoTipo): boolean {
    const alvo = this.normalizarTexto(doc.descricao);
    return this.documentosPericia().some(d =>
      this.normalizarTexto(d.descricao || '') === alvo ||
      this.normalizarTexto(d.tipo_documento || '') === this.normalizarTexto(doc.tipo_documento) &&
      this.normalizarTexto(d.descricao || '').includes(alvo.substring(0, 20))
    );
  }

  statusDocumentoPadrao(doc: DocumentoPadraoTipo): string {
    const alvo = this.normalizarTexto(doc.descricao);
    const encontrado = this.documentosPericia().find(d => this.normalizarTexto(d.descricao || '') === alvo);
    return encontrado?.status || (this.documentoPadraoExiste(doc) ? 'Cadastrado' : 'Pendente');
  }

  documentosPadraoPendentes(): DocumentoPadraoTipo[] {
    return this.documentosPadraoDaPericia().filter(d => !this.documentoPadraoExiste(d));
  }

  adicionarDocumentoPadrao(doc: DocumentoPadraoTipo): void {
    const atual = this.periciaAtual();
    if (!atual) return;

    if (this.documentoPadraoExiste(doc)) {
      this.mensagem.set('Documento padrão já consta na perícia.');
      return;
    }

    const payload: DocumentoPayload = {
      codigo: '',
      tipo_documento: doc.tipo_documento,
      fornecedor: 'Documento padrão',
      data_recebimento: '',
      formato: doc.formato,
      caminho_arquivo: '',
      hash_sha256: '',
      descricao: doc.descricao,
      observacoes: doc.obrigatorio ? 'Documento obrigatório conforme tipo de perícia.' : 'Documento recomendado conforme tipo de perícia.',
      status: 'Pendente'
    };

    this.service.criarDocumento(atual.id, payload).subscribe({
      next: () => {
        this.mensagem.set('Documento padrão adicionado como pendente.');
        this.carregarDocumentos();
      },
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível adicionar documento padrão.')
    });
  }

  adicionarDocumentosPadraoPendentes(): void {
    const pendentes = this.documentosPadraoPendentes();
    if (!pendentes.length) {
      this.mensagem.set('Todos os documentos padrão deste tipo de perícia já foram adicionados.');
      return;
    }

    pendentes.forEach(doc => this.adicionarDocumentoPadrao(doc));
  }


  novoDocumento(): void {
    this.documentoEditandoId = null;
    this.documentoForm = this.documentoVazio();
    this.arquivoDocumentoSelecionado = null;
    this.documentoFormVisivel = true;
  }

  cancelarDocumento(): void {
    this.documentoEditandoId = null;
    this.documentoForm = this.documentoVazio();
    this.arquivoDocumentoSelecionado = null;
    this.documentoFormVisivel = false;
  }

  editarDocumento(d: DocumentoPericia): void {
    this.documentoEditandoId = d.id;
    this.documentoFormVisivel = true;
    this.documentoForm = {
      codigo: d.codigo || '',
      tipo_documento: d.tipo_documento || '',
      descricao: d.descricao || '',
      fornecedor: d.fornecedor || '',
      data_recebimento: d.data_recebimento || '',
      formato: d.formato || '',
      caminho_arquivo: d.caminho_arquivo || '',
      hash_sha256: d.hash_sha256 || '',
      status: d.status || 'Pendente',
      observacoes: d.observacoes || ''
    };
  }

  salvarDocumento(): void {
    const atual = this.periciaAtual();
    if (!atual) return;

    const gravar = () => {
      if (this.documentoEditandoId) {
        this.service.atualizarDocumento(this.documentoEditandoId, this.documentoForm).subscribe({
          next: () => {
            this.mensagem.set('Documento atualizado.');
            this.cancelarDocumento();
            this.carregarDocumentos();
          },
          error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível atualizar o documento.')
        });
        return;
      }

      const payloadDocumento = { ...this.documentoForm, codigo: '' };
      this.service.criarDocumento(atual.id, payloadDocumento).subscribe({
        next: () => {
          this.mensagem.set('Documento cadastrado.');
          this.cancelarDocumento();
          this.carregarDocumentos();
        },
        error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível cadastrar o documento.')
      });
    };

    if (this.arquivoDocumentoSelecionado && !this.documentoForm.caminho_arquivo) {
      this.service.uploadDocumento(atual.id, this.arquivoDocumentoSelecionado).subscribe({
        next: (res) => {
          this.documentoForm.caminho_arquivo = res.caminho_arquivo || '';
          this.documentoForm.hash_sha256 = res.hash_sha256 || '';
          this.documentoForm.formato = res.formato || this.documentoForm.formato;
          gravar();
        },
        error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível enviar o arquivo antes de cadastrar.')
      });
      return;
    }

    gravar();
  }

  excluirDocumento(d: DocumentoPericia): void {
    if (!confirm(`Excluir documento ${d.codigo}?`)) return;

    this.service.excluirDocumento(d.id).subscribe({
      next: () => {
        this.mensagem.set('Documento excluído.');
        this.carregarDocumentos();
      },
      error: () => this.erro.set('Não foi possível excluir o documento.')
    });
  }

  carregarDiligencias(): void {
    const atual = this.periciaAtual();
    if (!atual) {
      this.diligenciasPericia.set([]);
      return;
    }

    this.service.listarDiligenciasPericia(atual.id).subscribe({
      next: dados => this.diligenciasPericia.set(dados),
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível carregar diligências. Verifique se a rota aparece no /docs e se a tabela pericia.diligencias existe.')
    });
  }

  novaDiligencia(): void {
    this.diligenciaEditandoId = null;
    this.diligenciaForm = this.diligenciaVazia();
    this.diligenciaFormVisivel = true;
  }

  cancelarDiligencia(): void {
    this.diligenciaEditandoId = null;
    this.diligenciaForm = this.diligenciaVazia();
    this.diligenciaFormVisivel = false;
  }

  editarDiligencia(d: DiligenciaPericia): void {
    this.diligenciaEditandoId = d.id;
    this.diligenciaFormVisivel = true;
    this.diligenciaForm = {
      codigo: d.codigo || '',
      data_envio: d.data_envio || '',
      destinatario: d.destinatario || '',
      solicitacao: d.solicitacao || '',
      prazo_resposta: d.prazo_resposta || '',
      status: d.status || 'Pendente',
      data_resposta: d.data_resposta || '',
      resposta_recebida: d.resposta_recebida || '',
      observacoes: d.observacoes || ''
    };
  }

  salvarDiligencia(): void {
    const atual = this.periciaAtual();
    if (!atual) return;

    if (this.diligenciaEditandoId) {
      this.service.atualizarDiligencia(this.diligenciaEditandoId, this.diligenciaForm).subscribe({
        next: () => {
          this.mensagem.set('Diligência atualizada.');
          this.cancelarDiligencia();
          this.carregarDiligencias();
        },
        error: () => this.erro.set('Não foi possível atualizar a diligência.')
      });
      return;
    }

    this.service.criarDiligencia(atual.id, this.diligenciaForm).subscribe({
      next: () => {
        this.mensagem.set('Diligência cadastrada.');
        this.cancelarDiligencia();
        this.carregarDiligencias();
      },
      error: () => this.erro.set('Não foi possível cadastrar a diligência.')
    });
  }

  excluirDiligencia(d: DiligenciaPericia): void {
    if (!confirm(`Excluir diligência ${d.codigo}?`)) return;

    this.service.excluirDiligencia(d.id).subscribe({
      next: () => {
        this.mensagem.set('Diligência excluída.');
        this.carregarDiligencias();
      },
      error: () => this.erro.set('Não foi possível excluir a diligência.')
    });
  }


carregarAnaliticoReal(): void {
  const atual = this.periciaAtual();
  if (!atual) {
    this.rotinasAnaliticas.set([]);
    this.execucoesAnaliticas.set([]);
    this.achadosAnaliticos.set([]);
    this.evidenciasMatriz.set([]);
    this.laudosChecklist.set([]);
    return;
  }

  this.analiticoReal.listarRotinas(atual.id).subscribe({
    next: dados => {
      this.rotinasAnaliticas.set(dados || []);
      if (!this.execucaoForm.rotina_id && dados?.length) {
        this.execucaoForm.rotina_id = dados[0].id;
      }
    },
    error: () => this.erro.set('Não foi possível carregar as rotinas analíticas. Verifique a tabela pericia.rotinas_analiticas.')
  });

  this.analiticoReal.listarExecucoes(atual.id).subscribe({
    next: dados => this.execucoesAnaliticas.set(dados || []),
    error: () => this.erro.set('Não foi possível carregar execuções analíticas.')
  });

  this.analiticoReal.listarAchados(atual.id).subscribe({
    next: dados => this.achadosAnaliticos.set(dados || []),
    error: () => this.erro.set('Não foi possível carregar achados analíticos.')
  });

  this.analiticoReal.listarEvidencias(atual.id).subscribe({
    next: dados => this.evidenciasMatriz.set(dados || []),
    error: () => this.erro.set('Não foi possível carregar matriz de evidências.')
  });

  this.analiticoReal.listarLaudosChecklist(atual.id).subscribe({
    next: dados => {
      const lista = dados || [];
      this.laudosChecklist.set(lista.slice(0, 1));
      this.laudoChecklistForm = lista.length ? { ...lista[0] } : this.laudoChecklistVazio();
      this.laudoFormVisivel = true;
    },
    error: () => this.erro.set('Não foi possível carregar checklist do laudo.')
  });
}

novaExecucaoAnalitica(): void {
  this.execucaoFormVisivel = true;
  this.execucaoForm = this.execucaoAnaliticaVazia();

  const primeiraRotina = this.rotinasAnaliticas()[0];
  if (primeiraRotina) {
    this.execucaoForm.rotina_id = primeiraRotina.id;
  }
}

salvarExecucaoAnalitica(): void {
  const atual = this.periciaAtual();
  if (!atual) return;

  if (!this.execucaoForm.rotina_id) {
    this.erro.set('Informe a rotina analítica da execução.');
    return;
  }

  this.analiticoReal.criarExecucao(atual.id, this.execucaoForm).subscribe({
    next: () => {
      this.mensagem.set('Execução analítica cadastrada.');
      this.execucaoFormVisivel = false;
      this.execucaoForm = this.execucaoAnaliticaVazia();
      this.carregarAnaliticoReal();
    },
    error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível cadastrar a execução analítica.')
  });
}

excluirExecucaoAnalitica(execucao: ExecucaoAnaliticaReal): void {
  if (!execucao.id) return;
  if (!confirm(`Excluir a execução analítica ${execucao.id}?`)) return;

  this.analiticoReal.excluirExecucao(execucao.id).subscribe({
    next: () => {
      this.mensagem.set('Execução analítica excluída.');
      this.carregarAnaliticoReal();
    },
    error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível excluir a execução. Verifique se existem achados vinculados.')
  });
}

novoAchadoAnalitico(): void {
  this.achadoFormVisivel = true;
  this.achadoAnaliticoForm = this.achadoAnaliticoVazio();

  const primeiraExecucao = this.execucoesAnaliticas()[0];
  if (primeiraExecucao?.id) {
    this.achadoAnaliticoForm.execucao_id = primeiraExecucao.id;
  }
}

salvarAchadoAnalitico(): void {
  const atual = this.periciaAtual();
  if (!atual) return;

  if (!this.achadoAnaliticoForm.execucao_id) {
    this.erro.set('Selecione a execução vinculada ao achado.');
    return;
  }

  if (!this.achadoAnaliticoForm.descricao.trim()) {
    this.erro.set('Informe a descrição do achado.');
    return;
  }

  this.analiticoReal.criarAchado(atual.id, this.achadoAnaliticoForm).subscribe({
    next: () => {
      this.mensagem.set('Achado analítico cadastrado.');
      this.achadoFormVisivel = false;
      this.achadoAnaliticoForm = this.achadoAnaliticoVazio();
      this.carregarAnaliticoReal();
    },
    error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível cadastrar o achado.')
  });
}

excluirAchadoAnalitico(achado: AchadoAnaliticoReal): void {
  if (!achado.id) return;
  if (!confirm(`Excluir o achado ${achado.id}?`)) return;

  this.analiticoReal.excluirAchado(achado.id).subscribe({
    next: () => {
      this.mensagem.set('Achado excluído.');
      this.carregarAnaliticoReal();
    },
    error: () => this.erro.set('Não foi possível excluir o achado.')
  });
}

novaEvidenciaMatriz(): void {
  this.evidenciaFormVisivel = true;
  this.evidenciaMatrizForm = this.evidenciaMatrizVazia();
}

salvarEvidenciaMatriz(): void {
  const atual = this.periciaAtual();
  if (!atual) return;

  this.analiticoReal.criarEvidencia(atual.id, this.evidenciaMatrizForm).subscribe({
    next: () => {
      this.mensagem.set('Registro de evidência/matriz cadastrado.');
      this.evidenciaFormVisivel = false;
      this.evidenciaMatrizForm = this.evidenciaMatrizVazia();
      this.carregarAnaliticoReal();
    },
    error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível cadastrar evidência/matriz.')
  });
}

excluirEvidenciaMatriz(item: MatrizEvidenciaReal): void {
  if (!item.id) return;
  if (!confirm(`Excluir o registro de matriz/evidência ${item.id}?`)) return;

  this.analiticoReal.excluirEvidencia(item.id).subscribe({
    next: () => {
      this.mensagem.set('Registro de evidência/matriz excluído.');
      this.carregarAnaliticoReal();
    },
    error: () => this.erro.set('Não foi possível excluir evidência/matriz.')
  });
}

novoLaudoChecklist(): void {
  // O checklist é único por perícia. Este método apenas carrega o checklist existente
  // ou prepara um checklist vazio.
  const existente = this.laudosChecklist()[0];
  this.laudoChecklistForm = existente ? { ...existente } : this.laudoChecklistVazio();
  this.laudoFormVisivel = true;
}

salvarLaudoChecklist(): void {
  const atual = this.periciaAtual();
  if (!atual) return;

  const existente = this.laudosChecklist()[0];

  if (existente?.id) {
    this.analiticoReal.atualizarLaudoChecklist(existente.id, this.laudoChecklistForm).subscribe({
      next: (salvo) => {
        this.mensagem.set('Checklist do laudo atualizado.');
        this.laudosChecklist.set([salvo]);
        this.laudoChecklistForm = { ...salvo };
      },
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível atualizar checklist do laudo.')
    });
    return;
  }

  this.analiticoReal.criarLaudoChecklist(atual.id, this.laudoChecklistForm).subscribe({
    next: (salvo) => {
      this.mensagem.set('Checklist do laudo criado.');
      this.laudosChecklist.set([salvo]);
      this.laudoChecklistForm = { ...salvo };
      this.laudoFormVisivel = true;
    },
    error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível criar checklist do laudo.')
  });
}

checklistLaudoFinalizado(): boolean {
  const c = this.laudoChecklistForm;
  return !!(
    c.laudo_concluido &&
    c.checklist_objeto_ok &&
    c.checklist_quesitos_ok &&
    c.checklist_documentos_ok &&
    c.checklist_evidencias_ok &&
    c.checklist_calculos_ok &&
    c.checklist_limitacoes_ok
  );
}

excluirLaudoChecklist(item: LaudoChecklistReal): void {
  // Checklist não deve ser excluído pela tela operacional.
  this.erro.set('O checklist é único por perícia e não deve ser excluído. Atualize os campos, se necessário.');
}



selecionarArquivoLaudo(event: Event): void {
  const input = event.target as HTMLInputElement;
  this.arquivoLaudoSelecionado = input.files && input.files.length ? input.files[0] : null;
}

anexarVersaoLaudo(): void {
  const atual = this.periciaAtual();
  if (!atual) {
    this.erro.set('Selecione uma perícia para anexar a versão do laudo.');
    return;
  }

  if (!this.arquivoLaudoSelecionado) {
    this.erro.set('Selecione o arquivo Word/PDF da versão alterada do laudo.');
    return;
  }

  const payload: DocumentoPayload = {
    codigo: '',
    tipo_documento: 'Laudo',
    fornecedor: 'PeritoDesk / versão ajustada',
    data_recebimento: new Date().toISOString().substring(0, 10),
    formato: this.arquivoLaudoSelecionado.name.toLowerCase().endsWith('.pdf') ? 'PDF' : 'DOCX',
    caminho_arquivo: this.arquivoLaudoSelecionado.name,
    hash_sha256: '',
    descricao: this.descricaoVersaoLaudo || `Versão alterada do laudo: ${this.arquivoLaudoSelecionado.name}`,
    observacoes: 'Arquivo anexado pela aba Laudo.',
    status: 'Recebido'
  };

  this.service.criarDocumento(atual.id, payload).subscribe({
    next: () => {
      this.mensagem.set('Versão do laudo registrada como documento. Consulte o histórico na aba Documentos.');
      this.arquivoLaudoSelecionado = null;
      this.descricaoVersaoLaudo = '';
      this.carregarDocumentos();
    },
    error: () => this.erro.set('Não foi possível registrar a versão do laudo.')
  });
}

versoesLaudoAnexadas(): DocumentoPericia[] {
  return this.documentosPericia().filter(d =>
    (d.tipo_documento || '').toLowerCase().includes('laudo') ||
    (d.descricao || '').toLowerCase().includes('laudo')
  );
}


visualizarLaudoWord(): void {
  if (!this.checklistLaudoFinalizado()) {
    this.erro.set('Finalize o checklist antes de visualizar o laudo.');
    return;
  }

  const atual = this.periciaAtual();
  if (!atual) {
    this.erro.set('Selecione uma perícia para visualizar o laudo.');
    return;
  }

  this.carregandoLaudoWord = true;
  this.erro.set('');

  fetch(`/api/pericias/${atual.id}/laudo-word/preview`)
    .then(resp => {
      if (!resp.ok) throw new Error('Falha ao gerar prévia do laudo.');
      return resp.json();
    })
    .then(dados => {
      this.laudoWordPreview = dados;
      this.carregandoLaudoWord = false;
})
    .catch(() => {
      this.erro.set('Não foi possível gerar a prévia do laudo Word.');
      this.carregandoLaudoWord = false;
});
}

baixarLaudoWord(): void {
  if (!this.checklistLaudoFinalizado()) {
    this.erro.set('Finalize o checklist antes de baixar o Word.');
    return;
  }

  const atual = this.periciaAtual();
  if (!atual) {
    this.erro.set('Selecione uma perícia para baixar o laudo.');
    return;
  }

  window.open(`/api/pericias/${atual.id}/laudo-word/download`, '_blank');
}

limparPreviewLaudoWord(): void {
  this.laudoWordPreview = null;
}


simNao(valor: boolean | null | undefined): string {
  return valor ? 'Sim' : 'Não';
}

  classeStatus(status: string | undefined | null): string {
    const texto = (status || '').toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-');
    return `status-${texto || 'padrao'}`;
  }


  fluxoEtapas(): EtapaFluxo[] {
    const pericia = this.periciaAtual();

    const etapas: EtapaFluxo[] = [];

    const add = (
      ordem: number,
      nome: string,
      aba: string,
      status: EtapaFluxo['status'],
      percentual: number,
      resumo: string,
      pendencias: string[] = [],
      proximaAcao?: string
    ) => etapas.push({ ordem, nome, aba, status, percentual, resumo, pendencias, proximaAcao });

    if (!pericia) return etapas;

    const aberturaPendencias: string[] = [];
    if (!pericia.codigo) aberturaPendencias.push('Código da perícia ainda não gerado.');
    if (!pericia.titulo) aberturaPendencias.push('Título não informado.');
    if (!pericia.numero_processo) aberturaPendencias.push('Número do processo não informado.');
    if (!pericia.cliente) aberturaPendencias.push('Cliente não informado.');
    const aberturaOk = aberturaPendencias.length === 0;
    add(1, 'Abertura', 'Perícia', aberturaOk ? 'Concluída' : 'Em andamento', aberturaOk ? 100 : 50,
      aberturaOk ? 'Dados principais cadastrados.' : 'Cadastro principal ainda incompleto.',
      aberturaPendencias,
      aberturaOk ? undefined : 'Completar os dados cadastrais da perícia.');

    const equipe = this.vinculosProfissionais();
    const temPerito = equipe.some(v => (v.papel || '').toLowerCase().includes('perito'));
    add(2, 'Equipe', 'Equipe',
      equipe.length === 0 ? 'Não iniciada' : temPerito ? 'Concluída' : 'Em andamento',
      equipe.length === 0 ? 0 : temPerito ? 100 : 60,
      equipe.length ? `${equipe.length} profissional(is) vinculado(s).` : 'Nenhum profissional vinculado.',
      equipe.length === 0 ? ['Nenhum profissional vinculado à perícia.'] : temPerito ? [] : ['Vincular ou identificar o perito responsável.'],
      equipe.length === 0 ? 'Vincular equipe/perito responsável.' : undefined);

    const quesitos = this.quesitos();
    const quesitosPendentes = quesitos.filter(q => ['Pendente', 'Em análise', 'Aguardando documento'].includes(q.status || '')).length;
    const quesitosRespondidos = quesitos.filter(q => ['Respondido', 'Prejudicado', 'Indeferido'].includes(q.status || '')).length;
    add(3, 'Quesitos', 'Quesitos',
      quesitos.length === 0 ? 'Não iniciada' : quesitosPendentes > 0 ? 'Em andamento' : 'Concluída',
      quesitos.length === 0 ? 0 : quesitosPendentes > 0 ? Math.round((quesitosRespondidos / quesitos.length) * 100) : 100,
      quesitos.length ? `${quesitos.length} quesito(s), ${quesitosPendentes} pendente(s).` : 'Nenhum quesito cadastrado.',
      quesitosPendentes > 0 ? [`${quesitosPendentes} quesito(s) pendente(s) de resposta.`] : [],
      quesitosPendentes > 0 ? 'Responder ou classificar os quesitos pendentes.' : undefined);

    const planejamentoStatus = this.planejamentoForm.status || '';
    const planejamentoOk = ['Aprovado', 'Concluído'].includes(planejamentoStatus);
    add(4, 'Planejamento', 'Planejamento',
      !planejamentoStatus ? 'Não iniciada' : planejamentoOk ? 'Concluída' : 'Em andamento',
      !planejamentoStatus ? 0 : planejamentoOk ? 100 : 60,
      planejamentoStatus ? `Status do planejamento: ${planejamentoStatus}.` : 'Planejamento ainda não preenchido.',
      planejamentoOk ? [] : ['Planejamento ainda não concluído/aprovado.'],
      planejamentoOk ? undefined : 'Concluir o planejamento da perícia.');

    const programasHonorarios = this.programas();
    const honorariosTotal = this.totalFinalHonorarios();
    const horasHonorarios = this.totalHorasProgramasHonorarios();
    const honorariosOk = programasHonorarios.length > 0 && horasHonorarios > 0 && honorariosTotal > 0;
    add(5, 'Honorários', 'Honorários',
      programasHonorarios.length === 0 ? 'Não iniciada' : honorariosOk ? 'Concluída' : 'Em andamento',
      programasHonorarios.length === 0 ? 0 : honorariosOk ? 100 : 60,
      programasHonorarios.length ? `${programasHonorarios.length} tarefa(s), ${horasHonorarios} hora(s), valor estimado R$ ${honorariosTotal.toFixed(2)}.` : 'Nenhuma tarefa cadastrada para estimar honorários.',
      programasHonorarios.length === 0 ? ['Cadastre tarefas/programas para estimar honorários.'] : honorariosOk ? [] : ['Informe horas estimadas e valor/hora nas tarefas.'],
      programasHonorarios.length === 0 ? 'Cadastrar tarefas padrão ou manuais.' : honorariosOk ? undefined : 'Completar horas e valor/hora em Programas/Tarefas.');

    const programas = this.programas();
    const tarefasAtrasadas = programas.filter(p => (p.status || '') === 'Atrasada').length;
    const tarefasAguardando = programas.filter(p => (p.status || '') === 'Aguardando documento').length;
    const tarefasConcluidas = programas.filter(p => ['Concluída', 'Cancelada'].includes(p.status || '')).length;
    let statusProgramas: EtapaFluxo['status'] = 'Não iniciada';
    if (programas.length) statusProgramas = tarefasAtrasadas ? 'Crítica' : tarefasAguardando ? 'Aguardando' : tarefasConcluidas === programas.length ? 'Concluída' : 'Em andamento';
    add(6, 'Programas/Tarefas', 'Programas/Tarefas',
      statusProgramas,
      programas.length === 0 ? 0 : Math.round((tarefasConcluidas / programas.length) * 100),
      programas.length ? `${programas.length} tarefa(s), ${tarefasConcluidas} concluída(s), ${tarefasAtrasadas} atrasada(s).` : 'Nenhuma tarefa cadastrada.',
      [
        ...(tarefasAtrasadas ? [`${tarefasAtrasadas} tarefa(s) atrasada(s).`] : []),
        ...(tarefasAguardando ? [`${tarefasAguardando} tarefa(s) aguardando documento.`] : [])
      ],
      tarefasAtrasadas ? 'Regularizar tarefas atrasadas.' : tarefasAguardando ? 'Acompanhar documentos pendentes das tarefas.' : undefined);

    const docs = this.documentosPericia();
    const docsPendentes = docs.filter(d => (d.status || '') === 'Pendente').length;
    const docsInconsistentes = docs.filter(d => (d.status || '') === 'Inconsistente').length;
    const docsOk = docs.filter(d => ['Recebido', 'Validado', 'Descartado'].includes(d.status || '')).length;
    let statusDocs: EtapaFluxo['status'] = 'Não iniciada';
    if (docs.length) statusDocs = docsInconsistentes ? 'Crítica' : docsPendentes ? 'Aguardando' : docsOk === docs.length ? 'Concluída' : 'Em andamento';
    add(7, 'Documentos', 'Documentos',
      statusDocs,
      docs.length === 0 ? 0 : Math.round((docsOk / docs.length) * 100),
      docs.length ? `${docs.length} documento(s), ${docsPendentes} pendente(s), ${docsInconsistentes} inconsistente(s).` : 'Nenhum documento cadastrado.',
      [
        ...(docsPendentes ? [`${docsPendentes} documento(s) pendente(s).`] : []),
        ...(docsInconsistentes ? [`${docsInconsistentes} documento(s) inconsistente(s).`] : [])
      ],
      docsInconsistentes ? 'Tratar documentos inconsistentes.' : docsPendentes ? 'Regularizar documentos pendentes.' : undefined);

    const diligencias = this.diligenciasPericia();
    const diligenciasAguardando = diligencias.filter(d => (d.status || '') === 'Aguardando resposta').length;
    const diligenciasVencidas = diligencias.filter(d => (d.status || '') === 'Vencida').length;
    const diligenciasOk = diligencias.filter(d => ['Respondida', 'Cancelada'].includes(d.status || '')).length;
    let statusDiligencias: EtapaFluxo['status'] = 'Não iniciada';
    if (diligencias.length) statusDiligencias = diligenciasVencidas ? 'Crítica' : diligenciasAguardando ? 'Aguardando' : diligenciasOk === diligencias.length ? 'Concluída' : 'Em andamento';
    add(8, 'Diligências', 'Diligências',
      statusDiligencias,
      diligencias.length === 0 ? 0 : Math.round((diligenciasOk / diligencias.length) * 100),
      diligencias.length ? `${diligencias.length} diligência(s), ${diligenciasAguardando} aguardando resposta.` : 'Nenhuma diligência cadastrada.',
      [
        ...(diligenciasVencidas ? [`${diligenciasVencidas} diligência(s) vencida(s).`] : []),
        ...(diligenciasAguardando ? [`${diligenciasAguardando} diligência(s) aguardando resposta.`] : [])
      ],
      diligenciasVencidas ? 'Tratar diligências vencidas.' : diligenciasAguardando ? 'Acompanhar diligências aguardando resposta.' : undefined);

    const execucoes = this.execucoesAnaliticas();
    const execErro = execucoes.filter(e => (e.status || '') === 'Erro').length;
    const execConcluidas = execucoes.filter(e => (e.status || '') === 'Concluída').length;
    let statusExec: EtapaFluxo['status'] = 'Não iniciada';
    if (execucoes.length) statusExec = execErro ? 'Crítica' : execConcluidas === execucoes.length ? 'Concluída' : 'Em andamento';
    add(9, 'Execuções Analíticas', 'Execuções',
      statusExec,
      execucoes.length === 0 ? 0 : Math.round((execConcluidas / execucoes.length) * 100),
      execucoes.length ? `${execucoes.length} execução(ões), ${execConcluidas} concluída(s), ${execErro} com erro.` : 'Nenhuma execução analítica cadastrada.',
      execErro ? [`${execErro} execução(ões) com erro.`] : [],
      execucoes.length === 0 ? 'Criar execução analítica conforme a rotina aplicável.' : execErro ? 'Reprocessar ou revisar execuções com erro.' : undefined);

    const achados = this.achadosAnaliticos();
    const achadosCriticos = achados.filter(a => ['Alta', 'Crítica'].includes(a.gravidade || '') && !a.revisado).length;
    const achadosRevisados = achados.filter(a => a.revisado).length;
    let statusAchados: EtapaFluxo['status'] = 'Não iniciada';
    if (achados.length) statusAchados = achadosCriticos ? 'Crítica' : achadosRevisados === achados.length ? 'Concluída' : 'Em andamento';
    add(10, 'Achados', 'Achados',
      statusAchados,
      achados.length === 0 ? 0 : Math.round((achadosRevisados / achados.length) * 100),
      achados.length ? `${achados.length} achado(s), ${achadosRevisados} revisado(s).` : 'Nenhum achado cadastrado.',
      achadosCriticos ? [`${achadosCriticos} achado(s) alto/crítico(s) sem revisão.`] : [],
      achadosCriticos ? 'Revisar achados altos ou críticos.' : undefined);

    const evidencias = this.evidenciasMatriz();
    const evidenciasComConclusao = evidencias.filter(e => !!(e.conclusao || '').trim()).length;
    const evidenciasLimitadas = evidencias.filter(e => !!(e.limitacao || '').trim()).length;
    let statusEvidencias: EtapaFluxo['status'] = 'Não iniciada';
    if (evidencias.length) statusEvidencias = evidenciasLimitadas ? 'Crítica' : evidenciasComConclusao === evidencias.length ? 'Concluída' : 'Em andamento';
    add(11, 'Evidências', 'Evidências',
      statusEvidencias,
      evidencias.length === 0 ? 0 : Math.round((evidenciasComConclusao / evidencias.length) * 100),
      evidencias.length ? `${evidencias.length} evidência(s), ${evidenciasComConclusao} com conclusão.` : 'Nenhuma evidência cadastrada.',
      [
        ...(evidencias.length && evidenciasComConclusao < evidencias.length ? [`${evidencias.length - evidenciasComConclusao} evidência(s) sem conclusão.`] : []),
        ...(evidenciasLimitadas ? [`${evidenciasLimitadas} evidência(s) com limitação.`] : [])
      ],
      evidenciasComConclusao < evidencias.length ? 'Completar conclusões da matriz de evidências.' : undefined);

    const checklistFinal = this.checklistLaudoFinalizado ? this.checklistLaudoFinalizado() : false;
    const checklistMarcados = [
      this.laudoChecklistForm.laudo_concluido,
      this.laudoChecklistForm.checklist_objeto_ok,
      this.laudoChecklistForm.checklist_quesitos_ok,
      this.laudoChecklistForm.checklist_documentos_ok,
      this.laudoChecklistForm.checklist_evidencias_ok,
      this.laudoChecklistForm.checklist_calculos_ok,
      this.laudoChecklistForm.checklist_limitacoes_ok
    ].filter(Boolean).length;
    add(12, 'Checklist', 'Checklist',
      checklistFinal ? 'Concluída' : checklistMarcados ? 'Em andamento' : 'Não iniciada',
      Math.round((checklistMarcados / 7) * 100),
      `${checklistMarcados}/7 itens obrigatórios marcados.`,
      checklistFinal ? [] : ['Checklist do laudo ainda não concluído.'],
      checklistFinal ? undefined : 'Concluir o checklist para liberar o laudo.');

    const versoesLaudo = this.versoesLaudoAnexadas ? this.versoesLaudoAnexadas().length : 0;
    add(13, 'Laudo', 'Laudo',
      !checklistFinal ? 'Bloqueada' : versoesLaudo ? 'Concluída' : 'Liberada',
      !checklistFinal ? 0 : versoesLaudo ? 100 : 80,
      !checklistFinal ? 'Laudo bloqueado até concluir checklist.' : versoesLaudo ? `${versoesLaudo} versão(ões) registrada(s).` : 'Laudo liberado para prévia/download.',
      !checklistFinal ? ['Checklist incompleto bloqueia a etapa de laudo.'] : [],
      !checklistFinal ? 'Finalizar checklist.' : versoesLaudo ? undefined : 'Gerar/baixar laudo e anexar versão ajustada.');

    const esclarecimentos = this.esclarecimentos();
    const escPendentes = this.esclarecimentosPendentes();
    const escConcluidos = esclarecimentos.filter(e => ['Concluído', 'Cancelado'].includes(e.status || '')).length;
    let statusEsc: EtapaFluxo['status'] = 'Não iniciada';
    if (esclarecimentos.length) statusEsc = escPendentes ? 'Em andamento' : escConcluidos === esclarecimentos.length ? 'Concluída' : 'Aguardando';
    add(14, 'Esclarecimentos', 'Esclarecimentos',
      statusEsc,
      esclarecimentos.length === 0 ? 0 : Math.round((escConcluidos / esclarecimentos.length) * 100),
      esclarecimentos.length ? `${esclarecimentos.length} pedido(s), ${escPendentes} pendente(s).` : 'Sem pedido de esclarecimento posterior ao laudo.',
      escPendentes ? [`${escPendentes} esclarecimento(s) pendente(s).`] : [],
      escPendentes ? 'Responder e protocolar esclarecimentos solicitados.' : undefined);

    return etapas;
  }

  fluxoAssistidoFases(): FaseFluxoAssistido[] {
    const pericia = this.periciaAtual();

    const semPericia = !pericia;
    const nomeacaoStatus = this.retornoNomeacaoForm?.status_nomeacao || 'Em análise';
    const nomeacaoAceita = this.nomeacaoAceita();
    const nomeacaoNaoAceita = nomeacaoStatus.startsWith('Não aceita');
    const honorariosStatus = this.statusHonorariosResumoFluxo();
    const honorariosLiberados = this.honorariosLiberamExecucao();
    const honorariosRejeitados = ['Impugnada', 'Não aprovada', 'Recusada'].includes(honorariosStatus);
    const honorariosEmEspera = ['Proposta apresentada', 'Aguardando manifestação das partes', 'Valor alterado pelo juiz', 'Depósito pendente'].includes(honorariosStatus);
    const planejamentoOk = ['Aprovado', 'Concluído'].includes(this.planejamentoForm?.status || '');
    const temDocumentos = this.documentosPericia().length > 0;
    const temExecucao = this.execucoesAnaliticas().length > 0;
    const temAchadosOuEvidencias = this.achadosAnaliticos().length > 0 || this.evidenciasMatriz().length > 0;
    const checklistOk = this.checklistLaudoFinalizado ? this.checklistLaudoFinalizado() : false;

    return [
      {
        ordem: 1,
        titulo: 'Nomeação e análise inicial',
        aba: 'Nomeação',
        status: nomeacaoAceita ? 'Concluída' : nomeacaoNaoAceita ? 'Encerrada' : 'Em andamento',
        decisao: nomeacaoAceita ? 'Encargo aceito.' : nomeacaoNaoAceita ? 'Encargo não aceito: gerar/guardar manifestação.' : 'Aguardando decisão do perito.',
        perguntas: [
          'Houve intimação regular da nomeação?',
          'Existe impedimento ou suspeição?',
          'O objeto está dentro da especialidade técnica?',
          'O perito aceitará o encargo?'
        ],
        proximaAcao: nomeacaoAceita ? 'Seguir para Honorários.' : nomeacaoNaoAceita ? 'Exportar manifestação e encerrar fluxo operacional.' : 'Responder as perguntas da nomeação e salvar.',
        observacaoNbc: 'A etapa registra aceite, escusa, impedimento/suspeição e capacidade técnica antes de avançar.'
      },
      {
        ordem: 2,
        titulo: 'Honorários periciais',
        aba: 'Honorários',
        status: !nomeacaoAceita ? 'Bloqueada' : honorariosLiberados ? 'Concluída' : ['Proposta apresentada', 'Aguardando manifestação das partes', 'Impugnada', 'Valor alterado pelo juiz', 'Depósito pendente'].includes(honorariosStatus) ? 'Aguardando' : 'Em andamento',
        decisao: !nomeacaoAceita ? 'Somente após aceite da nomeação.' : honorariosLiberados ? `Honorários liberam execução: ${honorariosStatus}.` : `Status atual: ${honorariosStatus}.`,
        perguntas: [
          'A proposta de honorários foi calculada?',
          'A proposta foi apresentada ao juízo/contratante?',
          'Houve impugnação ou alteração do valor?',
          'O valor foi fixado/aprovado ou houve depósito?'
        ],
        proximaAcao: !nomeacaoAceita ? 'Concluir a Nomeação.' : honorariosLiberados ? 'Liberar Planejamento e Execução.' : 'Salvar status dos honorários até fixação/aprovação.',
        observacaoNbc: 'A proposta deve refletir objeto, extensão, complexidade, horas, equipe, prazo e despesas.'
      },
      {
        ordem: 3,
        titulo: 'Planejamento pericial',
        aba: 'Planejamento',
        status: !honorariosLiberados ? 'Bloqueada' : planejamentoOk ? 'Concluída' : 'Em andamento',
        decisao: !honorariosLiberados ? 'Aguardando honorários fixados/aprovados.' : planejamentoOk ? 'Planejamento concluído/aprovado.' : 'Planejamento deve ser formalizado.',
        perguntas: [
          'O objeto da perícia está delimitado?',
          'Os quesitos foram cadastrados e organizados?',
          'O programa de trabalho e cronograma foram definidos?',
          'A equipe e responsabilidades estão definidas?'
        ],
        proximaAcao: !honorariosLiberados ? 'Resolver honorários.' : planejamentoOk ? 'Seguir para execução.' : 'Preencher planejamento, programas/tarefas, quesitos e cronograma.',
        observacaoNbc: 'O planejamento documenta escopo, metodologia, extensão dos trabalhos e recursos necessários.'
      },
      {
        ordem: 4,
        titulo: 'Execução, documentos e diligências',
        aba: 'Documentos',
        status: !planejamentoOk ? 'Bloqueada' : (temDocumentos || this.diligenciasPericia().length) ? 'Em andamento' : 'Liberada',
        decisao: !planejamentoOk ? 'Execução ainda bloqueada pelo planejamento.' : temDocumentos ? 'Documentos/diligências em tratamento.' : 'Execução liberada para coleta documental.',
        perguntas: [
          'Os documentos necessários foram recebidos?',
          'Há documentos pendentes ou inconsistentes?',
          'É necessária diligência para complementar prova?',
          'As respostas às diligências foram registradas?'
        ],
        proximaAcao: !planejamentoOk ? 'Concluir planejamento.' : temDocumentos ? 'Validar documentos e acompanhar diligências.' : 'Cadastrar documentos e diligências necessárias.',
        observacaoNbc: 'A execução reúne exames, indagações, diligências e demais procedimentos técnicos aplicáveis.'
      },
      {
        ordem: 5,
        titulo: 'Análises, achados e evidências',
        aba: 'Execuções',
        status: !planejamentoOk ? 'Bloqueada' : temExecucao && temAchadosOuEvidencias ? 'Concluída' : temExecucao ? 'Em andamento' : 'Liberada',
        decisao: temAchadosOuEvidencias ? 'Achados/evidências registrados.' : temExecucao ? 'Execução analítica iniciada.' : 'Aguardando rotinas/lançamentos analíticos.',
        perguntas: [
          'As rotinas analíticas foram executadas?',
          'Foram identificados achados relevantes?',
          'Cada achado possui evidência vinculada?',
          'As limitações foram registradas?'
        ],
        proximaAcao: temAchadosOuEvidencias ? 'Revisar suficiência das evidências.' : 'Executar análise e registrar achados/evidências.',
        observacaoNbc: 'As conclusões do laudo devem estar suportadas por evidências suficientes e registradas.'
      },
      {
        ordem: 6,
        titulo: 'Laudo, checklist e esclarecimentos',
        aba: 'Checklist',
        status: !temAchadosOuEvidencias ? 'Bloqueada' : checklistOk ? 'Concluída' : 'Em andamento',
        decisao: checklistOk ? 'Laudo liberado para versão final/exportação.' : 'Checklist do laudo ainda pendente.',
        perguntas: [
          'Todos os quesitos foram respondidos?',
          'As conclusões estão coerentes com as evidências?',
          'Limitações e ressalvas foram registradas?',
          'O checklist do laudo foi concluído?'
        ],
        proximaAcao: checklistOk ? 'Gerar laudo e registrar esclarecimentos se houver solicitação.' : 'Concluir checklist e preparar laudo.',
        observacaoNbc: 'O laudo deve apresentar metodologia, diligências, exame técnico, respostas aos quesitos e conclusão.'
      }
    ];
  }

  etapaAssistidaAtual(): FaseFluxoAssistido | null {
    return this.fluxoAssistidoFases().find(f => !['Concluída', 'Encerrada'].includes(f.status)) || this.fluxoAssistidoFases()[0] || null;
  }

  classeFluxoAssistido(status: string): string {
    return this.classeFluxo(status as EtapaFluxo['status']);
  }

  irParaFaseAssistida(fase: FaseFluxoAssistido): void {
    if (fase.status === 'Bloqueada') {
      this.mensagem.set(`Etapa bloqueada: ${fase.decisao}`);
      return;
    }
    this.selecionarAba(fase.aba);
  }

  aplicarDecisaoAssistida(fase: FaseFluxoAssistido, decisao: 'sim' | 'nao' | 'aguardar'): void {
    if (fase.titulo.includes('Nomeação')) {
      if (decisao === 'sim') {
        this.retornoNomeacaoForm.status_nomeacao = 'Aceita';
        this.retornoNomeacaoForm.tipo_retorno = 'Aceite da nomeação';
        this.retornoNomeacaoForm.motivo_nao_aceite = 'Perito declara ciência da nomeação, ausência de impedimento/suspeição identificado no momento e aceita o encargo.';
        this.aplicarModeloRetornoNomeacao();
        this.mensagem.set('Decisão aplicada: nomeação aceita. Revise e salve a Nomeação.');
      } else if (decisao === 'nao') {
        this.retornoNomeacaoForm.status_nomeacao = 'Não aceita — ausência de especialidade técnica';
        this.retornoNomeacaoForm.tipo_retorno = 'Escusa por falta de especialidade técnica';
        this.aplicarModeloRetornoNomeacao();
        this.mensagem.set('Decisão aplicada: não aceite. Revise o motivo, gere a manifestação e salve.');
      } else {
        this.retornoNomeacaoForm.status_nomeacao = 'Em análise';
        this.retornoNomeacaoForm.tipo_retorno = 'Aguardando análise';
        this.mensagem.set('Decisão aplicada: nomeação em análise.');
      }
      this.selecionarAba('Nomeação');
      return;
    }

    if (fase.titulo.includes('Honorários')) {
      if (decisao === 'sim') {
        this.honorariosForm.status_honorarios = 'Aprovada/Fixada';
        if (!this.honorariosForm.data_decisao) this.honorariosForm.data_decisao = new Date().toISOString().slice(0, 10);
        this.mensagem.set('Decisão aplicada: honorários aprovados/fixados. Revise valores e salve Honorários.');
      } else if (decisao === 'nao') {
        this.honorariosForm.status_honorarios = 'Impugnada';
        this.mensagem.set('Decisão aplicada: honorários impugnados/pendentes.');
      } else {
        this.honorariosForm.status_honorarios = 'Aguardando manifestação das partes';
        this.mensagem.set('Decisão aplicada: aguardando manifestação das partes.');
      }
      this.selecionarAba('Honorários');
      return;
    }

    this.irParaFaseAssistida(fase);
  }


  fluxoBpmnAssistidoNos(): NoBpmnAssistido[] {
    const pericia = this.periciaAtual();

    const semPericia = !pericia;
    const nomeacaoStatus = this.retornoNomeacaoForm?.status_nomeacao || 'Em análise';
    const nomeacaoAceita = this.nomeacaoAceita();
    const nomeacaoNaoAceita = nomeacaoStatus.startsWith('Não aceita');
    const honorariosStatus = this.statusHonorariosResumoFluxo();
    const honorariosLiberados = this.honorariosLiberamExecucao();
    const honorariosRejeitados = ['Impugnada', 'Não aprovada', 'Recusada'].includes(honorariosStatus);
    const honorariosEmEspera = ['Proposta apresentada', 'Aguardando manifestação das partes', 'Valor alterado pelo juiz', 'Depósito pendente'].includes(honorariosStatus);
    const planejamentoOk = ['Aprovado', 'Concluído'].includes(this.planejamentoForm?.status || '');
    const temEquipe = this.vinculosProfissionais().length > 0;
    const temQuesitos = this.quesitos().length > 0;
    const temDocumentos = this.documentosPericia().length > 0;
    const temDiligencias = this.diligenciasPericia().length > 0;
    const temProgramas = this.programas().length > 0;
    const temExecucao = this.execucoesAnaliticas().length > 0;
    const temAchados = this.achadosAnaliticos().length > 0;
    const temEvidencias = this.evidenciasMatriz().length > 0;
    const checklistOk = this.checklistLaudoFinalizado ? this.checklistLaudoFinalizado() : false;

    const fluxoInterrompidoJudicial = this.tipoFluxoBpmn === 'JUDICIAL' && (nomeacaoNaoAceita || honorariosRejeitados);
    const bloqueiaAposNomeacao = semPericia || (!nomeacaoAceita && this.tipoFluxoBpmn === 'JUDICIAL');
    const bloqueiaAposHonorarios = semPericia || fluxoInterrompidoJudicial || (!honorariosLiberados && this.tipoFluxoBpmn === 'JUDICIAL');
    const bloqueiaAposPlanejamento = semPericia || fluxoInterrompidoJudicial || !planejamentoOk;

    const comumInicio: NoBpmnAssistido[] = [
      {
        codigo: 'INICIO', tipo: 'inicio', titulo: 'Início', subtitulo: 'Iniciar perícia', aba: 'Perícia',
        status: semPericia ? 'Liberada' : 'Concluída', linha: 1, coluna: 3,
        decisao: 'Criar ou selecionar uma perícia para iniciar o roteiro.',
        perguntas: ['A perícia foi selecionada?', 'O tipo de atuação será judicial, assistente ou extrajudicial?', 'O objeto inicial está claro?'],
        proximaAcao: semPericia ? 'Clique em Início ou Cadastro da Perícia para criar a perícia pelo assistente.' : 'Avançar para Cadastro da Perícia.',
        referencia: 'Ponto de partida: o assistente orienta a sequência e abre a aba correta.'
      },
      {
        codigo: 'CADASTRO', tipo: 'tarefa', titulo: 'Cadastro da Perícia', subtitulo: 'Objeto, partes, prazo e processo', aba: 'Perícia',
        status: semPericia ? 'Liberada' : pericia?.id ? 'Concluída' : 'Em andamento', linha: 2, coluna: 3,
        decisao: 'Registrar objeto, partes, processo e prazo do trabalho.',
        perguntas: ['Há título e código da perícia?', 'Número do processo/cliente está correto?', 'Objeto inicial e prazo foram informados?', 'Partes e representantes foram cadastrados?'],
        proximaAcao: 'Concluir o cadastro e avançar para definir equipe e papéis.',
        referencia: 'Cadastro delimita objeto, extensão e finalidade do trabalho.'
      },
      {
        codigo: 'TIPO_ATUACAO', tipo: 'decisao', titulo: 'Tipo de atuação?', subtitulo: 'Juízo / Assistente / Extra', aba: 'Perícia',
        status: semPericia ? 'Liberada' : 'Concluída', linha: 3, coluna: 3,
        decisao: `Roteiro selecionado: ${this.tipoFluxoBpmnRotulo()}.`,
        perguntas: ['A atuação é como perito do juízo?', 'A atuação é como assistente técnico de uma parte?', 'É um trabalho extrajudicial contratado?'],
        proximaAcao: 'Selecione o tipo no topo do assistente e siga para equipe/papéis.',
        referencia: 'A natureza da atuação altera documentos, decisões, forma de entrega e responsabilidades.'
      },
      {
        codigo: 'EQUIPE', tipo: 'tarefa', titulo: 'Equipe / Papéis', subtitulo: 'Definir participantes e papéis', aba: 'Equipe',
        status: semPericia ? 'Bloqueada' : temEquipe ? 'Concluída' : 'Liberada', linha: 4, coluna: 3,
        decisao: 'Definir quem atua no trabalho e quais papéis técnicos serão exercidos.',
        perguntas: ['Perito/assistente principal foi identificado?', 'Apoio técnico foi vinculado?', 'Há competência e independência para o trabalho?'],
        proximaAcao: temEquipe ? 'Seguir para aceite, contratação ou escopo.' : 'Cadastrar ou vincular profissionais.',
        referencia: 'A equipe deve ser compatível com natureza, complexidade, responsabilidade e prazo da perícia.'
      }
    ];

    if (this.tipoFluxoBpmn === 'ASSISTENTE') {
      return [
        ...comumInicio,
        {
          codigo: 'CONTRATACAO_ASSISTENTE', tipo: 'tarefa', titulo: 'Contratação / Mandato', subtitulo: 'Parte e escopo', aba: 'Nomeação',
          status: semPericia ? 'Bloqueada' : 'Em andamento', linha: 5, coluna: 3,
          decisao: 'Validar contratação, escopo e limites de atuação como assistente técnico.',
          perguntas: ['Quem é a parte assistida?', 'Há procuração/contrato?', 'O escopo técnico está claro?'],
          proximaAcao: 'Registrar atuação e condições de trabalho.',
          referencia: 'O assistente técnico atua como auxiliar técnico da parte, mantendo zelo, responsabilidade e fundamentação técnica.'
        },
        {
          codigo: 'ANALISE_INICIAL', tipo: 'tarefa', titulo: 'Análise Inicial', subtitulo: 'Processo e documentos', aba: 'Planejamento',
          status: semPericia ? 'Bloqueada' : planejamentoOk ? 'Concluída' : 'Liberada', linha: 6, coluna: 3,
          decisao: 'Conhecer a controvérsia e mapear documentos, riscos e pontos técnicos.',
          perguntas: ['O processo foi lido?', 'Foram identificados pontos controvertidos?', 'Há documentos faltantes?'],
          proximaAcao: 'Organizar planejamento e estratégia técnica.',
          referencia: 'A análise inicial orienta o plano de trabalho e a elaboração de quesitos/parecer.'
        },
        {
          codigo: 'QUESITOS', tipo: 'tarefa', titulo: 'Quesitos / Impugnações', subtitulo: 'Apoio à parte', aba: 'Quesitos',
          status: semPericia ? 'Bloqueada' : temQuesitos ? 'Concluída' : 'Liberada', linha: 7, coluna: 3,
          decisao: 'Organizar quesitos e pontos de atenção para acompanhar a perícia.',
          perguntas: ['Quesitos foram sugeridos?', 'Há pontos técnicos para impugnação?', 'Há quesitos complementares?'],
          proximaAcao: 'Cadastrar quesitos e observações técnicas.',
          referencia: 'Quesitos devem ser objetivos, técnicos e aderentes aos pontos controvertidos.'
        },
        {
          codigo: 'DOCUMENTOS', tipo: 'tarefa', titulo: 'Documentos', subtitulo: 'Provas e anexos', aba: 'Documentos',
          status: semPericia ? 'Bloqueada' : temDocumentos ? 'Concluída' : 'Liberada', linha: 8, coluna: 2,
          decisao: 'Conferir documentos fornecidos pela parte e apontar lacunas.',
          perguntas: ['Documentos foram recebidos?', 'Há documentos faltantes?', 'A fonte é rastreável?'],
          proximaAcao: 'Cadastrar documentos e pendências.',
          referencia: 'A opinião técnica deve se apoiar em evidências suficientes e rastreáveis.'
        },
        {
          codigo: 'ACOMPANHAMENTO', tipo: 'tarefa', titulo: 'Acompanhamento', subtitulo: 'Diligências e perícia', aba: 'Diligências',
          status: semPericia ? 'Bloqueada' : temDiligencias ? 'Em andamento' : 'Liberada', linha: 8, coluna: 3,
          decisao: 'Acompanhar diligências, manifestações e atos periciais relevantes.',
          perguntas: ['Há diligências pendentes?', 'A parte precisa se manifestar?', 'O retorno foi registrado?'],
          proximaAcao: 'Registrar acompanhamento e retornos.',
          referencia: 'O acompanhamento documenta intervenções técnicas e preserva histórico do trabalho.'
        },
        {
          codigo: 'ANALISE_TECNICA', tipo: 'tarefa', titulo: 'Análise Técnica', subtitulo: 'Cálculos e achados', aba: 'Execuções',
          status: semPericia ? 'Bloqueada' : temExecucao ? 'Em andamento' : 'Liberada', linha: 9, coluna: 2,
          decisao: 'Executar cálculos, conciliações, simulações ou revisões técnicas.',
          perguntas: ['Os cálculos são auditáveis?', 'As premissas estão registradas?', 'Foram encontrados achados?'],
          proximaAcao: 'Executar análises e registrar achados.',
          referencia: 'A análise deve permitir revisão e sustentação objetiva das conclusões.'
        },
        {
          codigo: 'EVIDENCIAS', tipo: 'tarefa', titulo: 'Matriz de Evidências', subtitulo: 'Prova x conclusão', aba: 'Evidências',
          status: semPericia ? 'Bloqueada' : temEvidencias ? 'Concluída' : temAchados ? 'Liberada' : 'Bloqueada', linha: 9, coluna: 3,
          decisao: 'Relacionar documentos, achados e conclusões técnicas.',
          perguntas: ['Cada conclusão possui suporte?', 'As evidências são suficientes?', 'Há lacunas relevantes?'],
          proximaAcao: 'Montar matriz de evidências.',
          referencia: 'A matriz reforça coerência e rastreabilidade da manifestação técnica.'
        },
        {
          codigo: 'PARECER_ASSISTENTE', tipo: 'tarefa', titulo: 'Parecer Técnico', subtitulo: 'Manifestação da parte', aba: 'Laudo',
          status: !temEvidencias ? 'Bloqueada' : checklistOk ? 'Concluída' : 'Em andamento', linha: 10, coluna: 3,
          decisao: 'Preparar parecer, notas técnicas ou manifestação sobre laudo do perito.',
          perguntas: ['Conclusões estão fundamentadas?', 'Há resposta aos pontos relevantes?', 'O parecer está revisado?'],
          proximaAcao: 'Abrir Laudo/Parecer.',
          referencia: 'O parecer do assistente deve ser técnico, claro e vinculado às evidências analisadas.'
        },
        {
          codigo: 'FIM', tipo: 'fim', titulo: 'Fim', subtitulo: 'Entrega', aba: 'Laudo',
          status: checklistOk ? 'Concluída' : 'Bloqueada', linha: 11, coluna: 3,
          decisao: 'Concluir entrega e guardar memórias de trabalho.',
          perguntas: ['O parecer foi entregue?', 'Memórias foram preservadas?', 'Há nova manifestação?'],
          proximaAcao: 'Controlar esclarecimentos e arquivos.',
          referencia: 'A guarda das memórias facilita esclarecimentos posteriores.'
        }
      ];
    }

    if (this.tipoFluxoBpmn === 'EXTRAJUDICIAL') {
      return [
        ...comumInicio,
        {
          codigo: 'CONTRATACAO_EXTRA', tipo: 'tarefa', titulo: 'Contratação', subtitulo: 'Cliente e objetivo', aba: 'Nomeação',
          status: semPericia ? 'Bloqueada' : 'Em andamento', linha: 5, coluna: 3,
          decisao: 'Registrar contratante, objetivo, limites e entregável esperado.',
          perguntas: ['O contratante foi identificado?', 'O objetivo é viável?', 'O entregável foi combinado?'],
          proximaAcao: 'Formalizar escopo e condições.',
          referencia: 'No trabalho extrajudicial, o escopo e as limitações devem ser claros desde o início.'
        },
        {
          codigo: 'ESCOPO', tipo: 'tarefa', titulo: 'Escopo / Metodologia', subtitulo: 'Critérios e roteiro', aba: 'Planejamento',
          status: semPericia ? 'Bloqueada' : planejamentoOk ? 'Concluída' : 'Liberada', linha: 6, coluna: 3,
          decisao: 'Definir método, bases, critérios de cálculo e limitações.',
          perguntas: ['Método foi definido?', 'Critérios estão documentados?', 'Há limitação de informações?'],
          proximaAcao: 'Preencher planejamento e programa de trabalho.',
          referencia: 'A metodologia deve ser suficiente para sustentar o relatório ou parecer técnico.'
        },
        {
          codigo: 'DOCUMENTOS', tipo: 'tarefa', titulo: 'Documentos', subtitulo: 'Coleta e validação', aba: 'Documentos',
          status: semPericia ? 'Bloqueada' : temDocumentos ? 'Concluída' : 'Liberada', linha: 7, coluna: 2,
          decisao: 'Coletar bases, contratos, demonstrativos e documentos de suporte.',
          perguntas: ['A documentação está completa?', 'Bases foram validadas?', 'Há inconsistências?'],
          proximaAcao: 'Cadastrar documentos e pendências.',
          referencia: 'A suficiência documental condiciona o alcance das conclusões.'
        },
        {
          codigo: 'PROGRAMAS', tipo: 'tarefa', titulo: 'Programas / Tarefas', subtitulo: 'Procedimentos', aba: 'Programas/Tarefas',
          status: semPericia ? 'Bloqueada' : temProgramas ? 'Concluída' : 'Liberada', linha: 7, coluna: 3,
          decisao: 'Definir procedimentos técnicos, responsáveis e prazos.',
          perguntas: ['Tarefas foram planejadas?', 'Horas e prazos estão coerentes?', 'Procedimentos cobrem o escopo?'],
          proximaAcao: 'Adicionar tarefas padrão e específicas.',
          referencia: 'Programas documentam os procedimentos de exame, cálculo, revisão e conclusão.'
        },
        {
          codigo: 'EXECUCOES', tipo: 'tarefa', titulo: 'Execuções Analíticas', subtitulo: 'Cálculos e análises', aba: 'Execuções',
          status: semPericia ? 'Bloqueada' : temExecucao ? 'Em andamento' : 'Liberada', linha: 8, coluna: 2,
          decisao: 'Executar análises, cálculos, simulações e conciliações.',
          perguntas: ['Rotinas foram executadas?', 'Memórias foram geradas?', 'Premissas foram registradas?'],
          proximaAcao: 'Executar análises e registrar resultados.',
          referencia: 'As execuções devem ser rastreáveis e revisáveis.'
        },
        {
          codigo: 'ACHADOS', tipo: 'tarefa', titulo: 'Achados', subtitulo: 'Pontos técnicos', aba: 'Achados',
          status: semPericia ? 'Bloqueada' : temAchados ? 'Concluída' : temExecucao ? 'Liberada' : 'Bloqueada', linha: 8, coluna: 3,
          decisao: 'Registrar achados, indícios e divergências relevantes.',
          perguntas: ['Achados estão claros?', 'Há evidência associada?', 'A relevância foi indicada?'],
          proximaAcao: 'Registrar achados técnicos.',
          referencia: 'Achados devem se conectar às evidências e procedimentos executados.'
        },
        {
          codigo: 'RELATORIO_EXTRA', tipo: 'tarefa', titulo: 'Relatório / Parecer', subtitulo: 'Conclusões técnicas', aba: 'Laudo',
          status: !temAchados ? 'Bloqueada' : checklistOk ? 'Concluída' : 'Em andamento', linha: 10, coluna: 3,
          decisao: 'Preparar relatório, parecer ou memória técnica final.',
          perguntas: ['Metodologia foi explicada?', 'Conclusões têm suporte?', 'Limitações foram descritas?'],
          proximaAcao: 'Abrir Laudo/Parecer.',
          referencia: 'A entrega deve apresentar método, achados, limitações e conclusão técnica.'
        },
        {
          codigo: 'FIM', tipo: 'fim', titulo: 'Fim', subtitulo: 'Entrega', aba: 'Laudo',
          status: checklistOk ? 'Concluída' : 'Bloqueada', linha: 11, coluna: 3,
          decisao: 'Concluir entrega e guardar bases e memórias.',
          perguntas: ['Relatório foi entregue?', 'Bases foram preservadas?', 'Há revisão solicitada?'],
          proximaAcao: 'Controlar revisões e arquivos.',
          referencia: 'A guarda das bases e memórias dá suporte a revisões futuras.'
        }
      ];
    }

    return [
      ...comumInicio,
      {
        codigo: 'DECISAO_NOMEACAO', tipo: 'decisao', titulo: 'Aceita nomeação?', subtitulo: 'Sim / Não', aba: 'Nomeação',
        status: semPericia ? 'Bloqueada' : nomeacaoAceita ? 'Concluída' : nomeacaoNaoAceita ? 'Encerrada' : 'Em andamento', linha: 5, coluna: 3,
        decisao: nomeacaoAceita ? 'Nomeação aceita.' : nomeacaoNaoAceita ? 'Nomeação recusada com justificativa.' : 'Decidir aceite, escusa, suspeição ou impedimento.',
        perguntas: ['Existe impedimento ou suspeição?', 'Há capacidade técnica para o objeto?', 'O prazo é compatível?'],
        proximaAcao: nomeacaoAceita ? 'Seguir para honorários.' : nomeacaoNaoAceita ? 'Gerar manifestação e encerrar este fluxo.' : 'Abrir Nomeação e registrar a decisão.',
        referencia: 'Etapa norteada por independência, competência técnico-profissional, zelo e transparência.'
      },
      {
        codigo: 'NAO_ACEITA', tipo: 'fim', titulo: 'Encerrar / recusa', subtitulo: 'Escusa justificada', aba: 'Nomeação',
        status: nomeacaoNaoAceita ? 'Encerrada' : 'Bloqueada', linha: 5, coluna: 4,
        decisao: 'Caminho usado quando houver escusa, impedimento, suspeição ou impossibilidade técnica/prazo.',
        perguntas: ['O motivo foi registrado?', 'A manifestação foi revisada?', 'Foi exportada/protocolada?'],
        proximaAcao: 'Conferir manifestação na aba Nomeação.',
        referencia: 'A recusa deve ser fundamentada e documentada.'
      },
      {
        codigo: 'HONORARIOS', tipo: 'tarefa', titulo: 'Honorários', subtitulo: 'Estimativa e proposta', aba: 'Honorários',
        status: bloqueiaAposNomeacao ? 'Bloqueada' : honorariosLiberados ? 'Concluída' : honorariosRejeitados ? 'Encerrada' : honorariosEmEspera ? 'Aguardando' : 'Em andamento', linha: 6, coluna: 3,
        decisao: bloqueiaAposNomeacao ? 'Somente após aceite da nomeação.' : honorariosRejeitados ? 'Honorários não aceitos. O fluxo abaixo permanece bloqueado até reabertura/ajuste.' : `Status atual: ${honorariosStatus}.`,
        perguntas: ['Foram estimadas horas, complexidade, equipe e despesas?', 'A proposta foi gerada?', 'Foi apresentada ao juízo?'],
        proximaAcao: honorariosLiberados ? 'Seguir para planejamento.' : honorariosRejeitados ? 'O fluxo subsequente foi travado; revise Honorários para eventual ajuste/reapresentação.' : 'Revisar proposta e status de aprovação/fixação.',
        referencia: 'A estimativa deve guardar relação com objeto, complexidade, tempo, equipe, responsabilidade e despesas.'
      },
      {
        codigo: 'DECISAO_HONORARIOS', tipo: 'decisao', titulo: 'Honorários aprovados?', subtitulo: 'Sim / Aguardar', aba: 'Honorários',
        status: bloqueiaAposNomeacao ? 'Bloqueada' : honorariosLiberados ? 'Concluída' : honorariosRejeitados ? 'Encerrada' : 'Aguardando', linha: 7, coluna: 3,
        decisao: honorariosLiberados ? 'Honorários aprovados/fixados.' : honorariosRejeitados ? 'Honorários não aceitos. O fluxo abaixo permanece bloqueado.' : 'Aguardar decisão, manifestação ou ajuste.',
        perguntas: ['Houve aprovação/fixação?', 'Há impugnação?', 'O depósito/contratação está pendente?'],
        proximaAcao: honorariosLiberados ? 'Liberar planejamento.' : honorariosRejeitados ? 'Revisar a proposta ou aguardar nova deliberação em Honorários.' : 'Atualizar status em Honorários.',
        referencia: 'No fluxo judicial, a execução técnica pode depender da fixação/aprovação conforme o caso concreto.'
      },
      {
        codigo: 'AGUARDAR_HONORARIOS', tipo: 'espera', titulo: 'Aguardar / Ajustar', subtitulo: 'Pendência', aba: 'Honorários',
        status: honorariosRejeitados ? 'Encerrada' : !honorariosLiberados && !bloqueiaAposNomeacao ? 'Aguardando' : 'Bloqueada', linha: 7, coluna: 4,
        decisao: honorariosRejeitados ? 'A proposta não foi aceita. O processo abaixo foi bloqueado até nova revisão.' : 'Usado para impugnação, ajuste de proposta, manifestação das partes ou decisão do juízo.',
        perguntas: ['A pendência foi descrita?', 'Há prazo de manifestação?', 'O valor precisa ser recalculado?'],
        proximaAcao: honorariosRejeitados ? 'Revisar Honorários para eventual reapresentação.' : 'Atualizar proposta/status e retornar ao gateway de honorários.',
        referencia: 'Deve existir rastreabilidade da proposta, impugnações, ajustes e decisão/fixação.'
      },
      {
        codigo: 'PLANEJAMENTO', tipo: 'tarefa', titulo: 'Planejamento', subtitulo: 'Definir estratégia e plano', aba: 'Planejamento',
        status: bloqueiaAposHonorarios ? 'Bloqueada' : planejamentoOk ? 'Concluída' : 'Em andamento', linha: 8, coluna: 3,
        decisao: nomeacaoNaoAceita ? 'Fluxo encerrado por recusa da nomeação.' : honorariosRejeitados ? 'Fluxo bloqueado porque os honorários não foram aceitos.' : 'Definir alcance, metodologia, limitações, cronograma e responsáveis.',
        perguntas: ['O escopo foi delimitado?', 'A metodologia foi descrita?', 'Há cronograma e responsáveis?', 'Quais frentes podem iniciar em paralelo?'],
        proximaAcao: bloqueiaAposHonorarios ? 'Enquanto a etapa anterior não for regularizada, esta e as próximas permanecem bloqueadas.' : planejamentoOk ? 'Abrir preparação da execução: quesitos, documentos e diligências em paralelo.' : 'Concluir planejamento e liberar a preparação da execução.',
        referencia: 'Planejamento define natureza, oportunidade e extensão dos procedimentos.'
      },
      {
        codigo: 'QUESITOS', tipo: 'tarefa', titulo: 'Quesitos', subtitulo: 'Perguntas técnicas', aba: 'Quesitos',
        status: bloqueiaAposPlanejamento ? 'Bloqueada' : temQuesitos ? 'Concluída' : 'Liberada', linha: 9, coluna: 2,
        decisao: bloqueiaAposPlanejamento ? (nomeacaoNaoAceita ? 'Fluxo encerrado por recusa da nomeação.' : honorariosRejeitados ? 'Fluxo bloqueado porque os honorários não foram aceitos.' : 'Etapa bloqueada até concluir o planejamento.') : 'Organizar perguntas técnicas que orientarão exames e respostas.',
        perguntas: ['Quesitos do juízo foram cadastrados?', 'Quesitos das partes foram separados?', 'Há quesitos prejudicados ou repetidos?', 'Cada quesito aponta para documento/procedimento?'],
        proximaAcao: 'Cadastrar quesitos e vincular aos documentos/procedimentos.',
        referencia: 'Os quesitos delimitam pontos controvertidos relevantes.'
      },
      {
        codigo: 'DOCUMENTOS', tipo: 'tarefa', titulo: 'Documentos', subtitulo: 'Provas e anexos', aba: 'Documentos',
        status: bloqueiaAposPlanejamento ? 'Bloqueada' : temDocumentos ? 'Concluída' : 'Liberada', linha: 9, coluna: 3,
        decisao: 'Receber, conferir e relacionar documentos necessários.',
        perguntas: ['Documentos necessários foram listados?', 'Arquivos foram anexados?', 'Há pendência documental?', 'Cada documento tem origem e relação com o objeto?'],
        proximaAcao: 'Cadastrar documentos recebidos, pendentes e solicitar complementos.',
        referencia: 'O trabalho técnico deve se apoiar em elementos documentais suficientes e rastreáveis.'
      },
      {
        codigo: 'DILIGENCIAS', tipo: 'tarefa', titulo: 'Diligências', subtitulo: 'Solicitações', aba: 'Diligências',
        status: bloqueiaAposPlanejamento ? 'Bloqueada' : temDiligencias ? 'Em andamento' : 'Liberada', linha: 9, coluna: 4,
        decisao: 'Solicitar complementos, esclarecimentos ou documentos.',
        perguntas: ['Há necessidade de termo de diligência?', 'O prazo de retorno foi definido?', 'O retorno foi registrado?', 'A pendência impacta quesitos ou programas?'],
        proximaAcao: 'Cadastrar diligências e acompanhar respostas antes das conclusões.',
        referencia: 'Diligências suportam a obtenção de informações necessárias à conclusão técnica.'
      },
      {
        codigo: 'PROGRAMAS', tipo: 'tarefa', titulo: 'Programas / Tarefas', subtitulo: 'Procedimentos', aba: 'Programas/Tarefas',
        status: bloqueiaAposPlanejamento ? 'Bloqueada' : temProgramas ? 'Concluída' : 'Liberada', linha: 9, coluna: 5,
        decisao: 'Transformar quesitos, documentos e diligências em tarefas executáveis.',
        perguntas: ['Tarefas padrão foram adicionadas?', 'Tarefas específicas foram incluídas?', 'Horas estimadas estão coerentes?', 'Cada tarefa tem responsável, prazo e evidência esperada?'],
        proximaAcao: 'Adicionar tarefas padrão, revisar cronograma e liberar execuções analíticas.',
        referencia: 'Programas de trabalho documentam procedimentos de exame, investigação, cálculo, revisão e conclusão.'
      },
      {
        codigo: 'EXECUCOES', tipo: 'tarefa', titulo: 'Execuções Analíticas', subtitulo: 'Cálculos e análises', aba: 'Execuções',
        status: bloqueiaAposPlanejamento ? 'Bloqueada' : temExecucao ? 'Em andamento' : 'Liberada', linha: 10, coluna: 2,
        decisao: 'Executar análises, cálculos e rotinas com rastreabilidade.',
        perguntas: ['Rotinas foram executadas?', 'Memórias foram geradas?', 'Limitações foram registradas?', 'Resultados apontam achados ou evidências?'],
        proximaAcao: 'Executar análises, revisar memórias e registrar achados.',
        referencia: 'Execuções devem permitir revisão, rastreabilidade e sustentação das conclusões.'
      },
      {
        codigo: 'ACHADOS', tipo: 'tarefa', titulo: 'Achados', subtitulo: 'Pontos técnicos', aba: 'Achados',
        status: bloqueiaAposPlanejamento ? 'Bloqueada' : temAchados ? 'Concluída' : temExecucao ? 'Liberada' : 'Bloqueada', linha: 10, coluna: 3,
        decisao: 'Registrar fatos técnicos identificados nas execuções.',
        perguntas: ['Achados estão claros?', 'A gravidade/relevância foi indicada?', 'Há vínculo com evidência?'],
        proximaAcao: 'Registrar achados técnicos.',
        referencia: 'Achados devem estar vinculados a evidências e procedimentos executados.'
      },
      {
        codigo: 'EVIDENCIAS', tipo: 'tarefa', titulo: 'Matriz de Evidências', subtitulo: 'Prova x conclusão', aba: 'Evidências',
        status: bloqueiaAposPlanejamento ? 'Bloqueada' : temEvidencias ? 'Concluída' : temAchados ? 'Liberada' : 'Bloqueada', linha: 10, coluna: 4,
        decisao: 'Vincular achados, documentos e conclusões.',
        perguntas: ['Cada achado possui evidência?', 'As fontes estão identificadas?', 'Há suficiência para conclusão?'],
        proximaAcao: 'Montar matriz de evidências.',
        referencia: 'A matriz reforça coerência, rastreabilidade, suficiência e revisão do trabalho.'
      },
      {
        codigo: 'LAUDO', tipo: 'tarefa', titulo: 'Laudo / Parecer', subtitulo: 'Redigir e finalizar', aba: 'Checklist',
        status: !temEvidencias ? 'Bloqueada' : checklistOk ? 'Concluída' : 'Em andamento', linha: 11, coluna: 3,
        decisao: 'Conferir checklist e preparar laudo/parecer técnico.',
        perguntas: ['Quesitos foram respondidos?', 'Metodologia foi descrita?', 'Conclusões estão vinculadas?', 'Checklist foi concluído?'],
        proximaAcao: checklistOk ? 'Gerar/exportar laudo.' : 'Concluir checklist do laudo.',
        referencia: 'O laudo/parecer deve conter metodologia, exames, diligências, respostas e conclusão técnica.'
      },
      {
        codigo: 'FIM', tipo: 'fim', titulo: 'Fim', subtitulo: 'Entrega', aba: 'Laudo',
        status: checklistOk ? 'Concluída' : 'Bloqueada', linha: 12, coluna: 3,
        decisao: 'Finalizar entrega e preservar memórias de trabalho.',
        perguntas: ['Laudo foi exportado?', 'Arquivos e memórias foram preservados?', 'Há pedido de esclarecimentos?'],
        proximaAcao: 'Abrir Laudo ou Esclarecimentos.',
        referencia: 'Após a entrega, podem existir esclarecimentos, complementações ou guarda dos papéis de trabalho.'
      }
    ];
  }

  tipoFluxoBpmnRotulo(): string {
    if (this.tipoFluxoBpmn === 'ASSISTENTE') return 'Assistente Técnico';
    if (this.tipoFluxoBpmn === 'EXTRAJUDICIAL') return 'Extrajudicial';
    return 'Perito do Juízo';
  }

  selecionarTipoFluxoBpmn(tipo: 'JUDICIAL' | 'ASSISTENTE' | 'EXTRAJUDICIAL'): void {
    this.tipoFluxoBpmn = tipo;
    this.noBpmnSelecionadoCodigo = 'INICIO';
  }

  ctaSemPericiaBpmn(): void {
    this.mostrarPesquisaPericias = true;
    this.mostrarListaPericias = true;
    this.mensagem.set('Selecione uma perícia na lista ou clique em Nova perícia para iniciar pelo assistente BPMN.');
  }

  noBpmnSelecionado(): NoBpmnAssistido | null {
    const nos = this.fluxoBpmnAssistidoNos();
    return nos.find(no => no.codigo === this.noBpmnSelecionadoCodigo) || nos[0] || null;
  }

  selecionarNoBpmn(no: NoBpmnAssistido): void {
    this.noBpmnSelecionadoCodigo = no.codigo;
    if (!this.periciaAtual() && (no.codigo === 'INICIO' || no.codigo === 'CADASTRO')) {
      this.iniciarPericiaPeloBpmn();
    }
  }

  // v92.2.7: apoio ao BPMN estático em SVG com hotspots clicáveis.
  selecionarNoBpmnPorCodigo(codigo: string): void {
    const no = this.fluxoBpmnAssistidoNos().find(item => item.codigo === codigo);
    if (no) {
      this.selecionarNoBpmn(no);
    }
  }

  abrirNoBpmnPorCodigo(codigo: string): void {
    const no = this.fluxoBpmnAssistidoNos().find(item => item.codigo === codigo);
    if (no) {
      this.abrirNoBpmn(no);
    }
  }

  novaPericiaAssistida(): void {
    this.erro.set('');
    this.mensagem.set('Fluxo BPMN aberto. Clique no círculo Início para começar a nova perícia assistida.');
    this.periciaSelecionadaId = null;
    this.modo.set('visualizar');
    this.abaAtiva.set('Fluxo Assistido');
    this.noBpmnSelecionadoCodigo = 'INICIO';
    this.mostrarListaPericias = false;
    this.mostrarPesquisaPericias = false;
    this.limparDadosPericiaSelecionada();
  }

  iniciarPericiaPeloBpmn(): void {
    this.novaPericiaIniciadaPeloBpmn = true;
    this.noBpmnSelecionadoCodigo = 'CADASTRO';
    this.iniciarNovo();
    this.mensagem.set('Fluxo BPMN iniciado. Preencha o cadastro da perícia e salve para voltar ao assistente e definir o tipo de atuação.');
  }

  abrirNoBpmn(no: NoBpmnAssistido | null): void {
    if (!no) return;
    this.noBpmnSelecionadoCodigo = no.codigo;

    if (!this.periciaAtual() && (no.codigo === 'INICIO' || no.codigo === 'CADASTRO')) {
      this.iniciarPericiaPeloBpmn();
      return;
    }

    if (no.status === 'Bloqueada') {
      this.mensagem.set(`Etapa bloqueada: ${no.decisao}`);
      return;
    }
    this.selecionarAba(no.aba);
  }

  classeNoBpmn(no: NoBpmnAssistido): string {
    return `${no.tipo} ${this.classeFluxoAssistido(no.status)}`;
  }

  // v92.2.9 — SVG premium dinâmico: status real, preparação paralela, execução estruturada e painel refinado.
  classeSvgNoBpmn(codigo: string): string {
    const no = this.fluxoBpmnAssistidoNos().find(item => item.codigo === codigo);
    const status = no ? this.classeFluxoAssistido(no.status) : 'status-bloqueada';
    const selecionado = this.noBpmnSelecionadoCodigo === codigo ? 'selected' : '';
    const tipo = no?.tipo || 'tarefa';
    return `${tipo} ${status} ${selecionado}`.trim();
  }

  estiloNoBpmn(no: NoBpmnAssistido): Record<string, string> {
    // v92.2.6: implementação premium fiel ao mockup.
    // Regra: eixo principal central fixo, decisões sem rotação visual por transform,
    // exceções sempre à direita e execução em fileira limpa.
    const posicoes: Record<string, { x: number; y: number; w: number; h: number }> = {
      INICIO: { x: 665, y: 28, w: 72, h: 72 },
      CADASTRO: { x: 560, y: 110, w: 280, h: 58 },
      TIPO_ATUACAO: { x: 615, y: 200, w: 170, h: 96 },
      EQUIPE: { x: 560, y: 325, w: 280, h: 58 },
      NOMEACAO: { x: 615, y: 420, w: 170, h: 96 },
      RECUSA: { x: 930, y: 435, w: 210, h: 58 },
      HONORARIOS: { x: 560, y: 540, w: 280, h: 58 },
      GATEWAY_HONORARIOS: { x: 615, y: 635, w: 170, h: 96 },
      AGUARDAR_HONORARIOS: { x: 930, y: 650, w: 210, h: 58 },
      PLANEJAMENTO: { x: 560, y: 755, w: 280, h: 58 },
      QUESITOS: { x: 150, y: 890, w: 120, h: 58 },
      DOCUMENTOS: { x: 300, y: 890, w: 125, h: 58 },
      DILIGENCIAS: { x: 455, y: 890, w: 125, h: 58 },
      PROGRAMAS: { x: 610, y: 890, w: 140, h: 58 },
      EXECUCOES: { x: 780, y: 890, w: 140, h: 58 },
      ACHADOS: { x: 950, y: 890, w: 125, h: 58 },
      EVIDENCIAS: { x: 1085, y: 890, w: 150, h: 58 },
      LAUDO: { x: 1250, y: 890, w: 140, h: 58 },
      FIM: { x: 1282, y: 1000, w: 76, h: 76 }
    };
    const p = posicoes[no.codigo] || { x: 620, y: 160 + no.linha * 120, w: 240, h: 64 };
    return {
      left: `${p.x}px`,
      top: `${p.y}px`,
      width: `${p.w}px`,
      height: `${p.h}px`
    };
  }

  percentualFluxo(): number {
    const etapas = this.fluxoEtapas();
    if (!etapas.length) return 0;
    return Math.round(etapas.reduce((s, e) => s + e.percentual, 0) / etapas.length);
  }

  etapaAtualFluxo(): EtapaFluxo | null {
    return this.fluxoEtapas().find(e => !['Concluída'].includes(e.status)) || this.fluxoEtapas()[this.fluxoEtapas().length - 1] || null;
  }

  pendenciasFluxo(): string[] {
    return this.fluxoEtapas().flatMap(e => e.pendencias.map(p => `${e.nome}: ${p}`));
  }

  proximaAcaoFluxo(): string {
    const etapa = this.fluxoEtapas().find(e => e.proximaAcao);
    return etapa?.proximaAcao || 'Fluxo sem pendências principais. Perícia pronta para revisão final.';
  }

  irParaEtapaFluxo(etapa: EtapaFluxo): void {
    this.selecionarAba(etapa.aba);
  }

  classeFluxo(status: string): string {
    return this.classeStatus(status);
  }


  registrosDaAba(): RegistroTexto[] {
    const atual = this.periciaAtual();
    if (!atual) return [];
    switch (this.abaAtiva()) {
      case 'Quesitos': return atual.quesitos;
      case 'Planejamento': return atual.planejamentos;
      case 'Programas/Tarefas': return atual.programas;
      case 'Papéis': return atual.papeis;
      case 'Documentos': return atual.documentos;
      case 'Diligências': return atual.diligencias;
      case 'Execuções': return atual.execucoes;
      case 'Achados': return atual.achados;
      case 'Evidências': return atual.evidencias;
      case 'Checklist': return atual.laudo;
      case 'Laudo': return atual.laudo;
      default: return [];
    }
  }

  private normalizarTexto(valor: string): string {
    return (valor || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  private extrairAnoMes(pericia: Pericia): { ano: string; mes: string } {
    const codigo = pericia.codigo || '';
    const anoCodigo = codigo.match(/^PER-(\d{4})-/)?.[1] || '';

    const dataCadastro =
      (pericia as any).created_at ||
      pericia.data_inicio ||
      pericia.data_base ||
      pericia.prazo_laudo ||
      '';

    const mesCadastro = /^\d{4}-\d{2}/.test(dataCadastro) ? dataCadastro.substring(5, 7) : '';

    if (anoCodigo) return { ano: anoCodigo, mes: mesCadastro };
    if (/^\d{4}-\d{2}/.test(dataCadastro)) {
      return { ano: dataCadastro.substring(0, 4), mes: mesCadastro };
    }

    return { ano: '', mes: '' };
  }

  private preencherFormulario(pericia: Pericia): void {
    this.formulario = {
      codigo: pericia.codigo || '',
      titulo: pericia.titulo || '',
      tipo_pericia: pericia.tipo_pericia || 'Perícia Contábil',
      cliente: pericia.cliente || '',
      numero_processo: pericia.numero_processo || '',
      vara_ou_camara: pericia.vara_ou_camara || '',
      data_inicio: pericia.data_inicio || '',
      data_base: pericia.data_base || '',
      prazo_laudo: pericia.prazo_laudo || '',
      status: pericia.status || 'Planejamento',
      objeto_inicial: pericia.objeto_inicial || '',
      descricao: pericia.descricao || '',
    };
  }

  private carregarFormularioSelecionada(): void {
    const p = this.periciaAtual();
    if (!p) {
      this.formulario = this.formularioVazio();
      return;
    }
    this.preencherFormulario(p);
  }

  private documentoVazio(): DocumentoPayload {
    return {
      codigo: '',
      tipo_documento: 'Extrato bancário',
      descricao: '',
      fornecedor: '',
      data_recebimento: '',
      formato: 'PDF',
      caminho_arquivo: '',
      hash_sha256: '',
      status: 'Pendente',
      observacoes: ''
    };
  }

  private diligenciaVazia(): DiligenciaPayload {
    return {
      codigo: '',
      data_envio: '',
      destinatario: '',
      solicitacao: '',
      prazo_resposta: '',
      status: 'Pendente',
      data_resposta: '',
      resposta_recebida: '',
      observacoes: ''
    };
  }

  private planejamentoVazio(): PlanejamentoPayload {
    return {
      objeto_pericia: '',
      escopo: '',
      limites: '',
      criterios_tecnicos: '',
      documentos_necessarios: '',
      riscos_limitacoes: '',
      cronograma: '',
      equipe_apoio: '',
      produtos_finais: '',
      status: 'Em elaboração',
      data_conclusao: ''
    };
  }

  private programaVazio(): ProgramaTrabalhoPayload {
    return {
      codigo: '',
      ordem: 0,
      etapa: 'Análise inicial',
      tarefa: '',
      descricao: '',
      procedimento: 'Exame',
      documento_necessario: '',
      responsavel: '',
      data_prevista: '',
      data_conclusao: '',
      status: 'Pendente',
      resultado: '',
      observacoes: '',
      horas_estimadas: 0,
      valor_hora: Number(this.valorHoraBaseHonorarios || 0),
      despesas_honorarios: 0
    };
  }

  private tarefaVazia(): TarefaPericiaPayload {
    return {
      fase: 'Planejamento',
      titulo: '',
      responsavel: '',
      data_prevista: '',
      status: 'Pendente',
      pendencia: ''
    };
  }

  private quesitoVazio(): QuesitoPayload {
    return {
      codigo: '',
      numero: 0,
      origem: 'Juízo',
      texto: '',
      status: 'Pendente',
      resposta_resumo: ''
    };
  }


private execucaoAnaliticaVazia(): ExecucaoAnaliticaReal {
  return {
    rotina_id: null,
    status: 'Registrada',
    parametros_json: '',
    resumo_json: '',
    texto_tecnico: '',
    total_registros: null,
    total_achados: null,
    arquivo_entrada_principal: '',
    arquivo_saida_resumo: '',
    arquivo_saida_achados: '',
    log_execucao: ''
  };
}

private achadoAnaliticoVazio(): AchadoAnaliticoReal {
  return {
    execucao_id: null,
    tipo_achado: 'Inconsistência',
    gravidade: 'Média',
    descricao: '',
    evidencia: '',
    valor_relacionado: '',
    linha_origem: '',
    recomendacao: '',
    confirmado: false,
    revisado: false
  };
}

private evidenciaMatrizVazia(): MatrizEvidenciaReal {
  return {
    quesito_id: null,
    documento_id: null,
    papel_trabalho_id: null,
    procedimento: '',
    achado: '',
    conclusao: '',
    limitacao: ''
  };
}

private laudoChecklistVazio(): LaudoChecklistReal {
  return {
    laudo_concluido: false,
    comunicacao_assistente: false,
    diligencia_conjunta: false,
    checklist_objeto_ok: false,
    checklist_quesitos_ok: false,
    checklist_documentos_ok: false,
    checklist_evidencias_ok: false,
    checklist_calculos_ok: false,
    checklist_limitacoes_ok: false,
    observacoes: '',
    status: 'Em elaboração'
  };
}


  private retornoNomeacaoVazio(): RetornoNomeacaoPayload {
    return {
      status_nomeacao: 'Em análise',
      tipo_retorno: 'Aguardando análise',
      data_intimacao: '',
      data_resposta: new Date().toISOString().slice(0, 10),
      motivo_nao_aceite: '',
      fundamentacao: '',
      texto_manifestacao: '',
      status_protocolo: 'Rascunho',
      observacoes_internas: ''
    };
  }

  async carregarRetornoNomeacao(): Promise<void> {
    const pericia = this.periciaAtual();
    if (!pericia?.id) {
      this.retornoNomeacaoForm = this.retornoNomeacaoVazio();
      return;
    }
    try {
      const retorno = await firstValueFrom(this.service.obterRetornoNomeacao(pericia.id));
      this.retornoNomeacaoForm = retorno ? {
        status_nomeacao: retorno.status_nomeacao || 'Em análise',
        tipo_retorno: retorno.tipo_retorno || this.tipoRetornoPadraoPorStatus(retorno.status_nomeacao || 'Em análise'),
        data_intimacao: retorno.data_intimacao || '',
        data_resposta: retorno.data_resposta || new Date().toISOString().slice(0, 10),
        motivo_nao_aceite: retorno.motivo_nao_aceite || '',
        fundamentacao: retorno.fundamentacao || '',
        texto_manifestacao: retorno.texto_manifestacao || '',
        status_protocolo: retorno.status_protocolo || 'Rascunho',
        observacoes_internas: retorno.observacoes_internas || ''
      } : this.retornoNomeacaoVazio();
    } catch {
      this.retornoNomeacaoForm = this.retornoNomeacaoVazio();
      this.erro.set('Não foi possível carregar o retorno da nomeação.');
    }
  }


  private tipoRetornoPadraoPorStatus(status: string): string {
    if (status === 'Aceita') return 'Aceite da nomeação';
    if (status === 'Não aceita — impossibilidade de prazo') return 'Escusa por impossibilidade de prazo';
    if (status === 'Não aceita — impedimento' || status === 'Não aceita — suspeição') return 'Impedimento/Suspeição';
    if (status === 'Não aceita — conflito de interesse') return 'Conflito de interesse';
    if (status === 'Não aceita — outro motivo justificado') return 'Outro motivo justificado';
    if (status === 'Não aceita — ausência de especialidade técnica') return 'Escusa por falta de especialidade técnica';
    return 'Aguardando análise';
  }

  onStatusNomeacaoChange(status: string): void {
    this.retornoNomeacaoForm.status_nomeacao = status;
    this.retornoNomeacaoForm.tipo_retorno = this.tipoRetornoPadraoPorStatus(status);
    if (status === 'Aceita') {
      this.retornoNomeacaoForm.motivo_nao_aceite = 'Aceite da nomeação.';
      this.retornoNomeacaoForm.fundamentacao = 'Aceite do encargo pericial.';
    }
    if (status === 'Em análise') {
      this.retornoNomeacaoForm.motivo_nao_aceite = '';
      this.retornoNomeacaoForm.fundamentacao = '';
      this.retornoNomeacaoForm.texto_manifestacao = '';
    }
  }

  onTipoRetornoNomeacaoChange(tipo: string): void {
    this.retornoNomeacaoForm.tipo_retorno = tipo;
    if (tipo === 'Aceite da nomeação') {
      this.retornoNomeacaoForm.status_nomeacao = 'Aceita';
      return;
    }
    if (tipo === 'Aguardando análise') {
      this.retornoNomeacaoForm.status_nomeacao = 'Em análise';
      return;
    }
    if (tipo === 'Escusa por impossibilidade de prazo') {
      this.retornoNomeacaoForm.status_nomeacao = 'Não aceita — impossibilidade de prazo';
      return;
    }
    if (tipo === 'Impedimento/Suspeição') {
      this.retornoNomeacaoForm.status_nomeacao = 'Não aceita — impedimento';
      return;
    }
    if (tipo === 'Conflito de interesse') {
      this.retornoNomeacaoForm.status_nomeacao = 'Não aceita — conflito de interesse';
      return;
    }
    if (tipo === 'Outro motivo justificado') {
      this.retornoNomeacaoForm.status_nomeacao = 'Não aceita — outro motivo justificado';
      return;
    }
    this.retornoNomeacaoForm.status_nomeacao = 'Não aceita — ausência de especialidade técnica';
  }

  aplicarModeloRetornoNomeacao(): void {
    const pericia = this.periciaAtual();
    const numeroProcesso = pericia?.numero_processo || '__________';
    const vara = pericia?.vara_ou_camara || '___ Vara __________ da Comarca de __________';
    const requerente = pericia?.cliente || '__________';
    const tipo = this.retornoNomeacaoForm.tipo_retorno === 'Aguardando análise' ? this.tipoRetornoPadraoPorStatus(this.retornoNomeacaoForm.status_nomeacao) : (this.retornoNomeacaoForm.tipo_retorno || this.tipoRetornoPadraoPorStatus(this.retornoNomeacaoForm.status_nomeacao));
    let titulo = 'MANIFESTAÇÃO DE ESCUSA DO ENCARGO PERICIAL';
    let status = 'Não aceita — ausência de especialidade técnica';
    let motivo = this.retornoNomeacaoForm.motivo_nao_aceite || '';
    let fundamentacao = this.retornoNomeacaoForm.fundamentacao || '';
    let corpo = '';

    if (tipo === 'Aceite da nomeação') {
      titulo = 'MANIFESTAÇÃO DE ACEITE DA NOMEAÇÃO PERICIAL';
      status = 'Aceita';
      motivo = 'Aceite da nomeação.';
      fundamentacao = 'Aceite do encargo pericial.';
      corpo = 'O profissional manifesta o aceite da nomeação pericial e informa que, em análise inicial, não identificou impedimento ou suspeição que inviabilize sua atuação. Requer o regular prosseguimento do feito, com as providências necessárias à realização da prova pericial.';
    } else if (tipo === 'Escusa por impossibilidade de prazo') {
      status = 'Não aceita — impossibilidade de prazo';
      motivo = motivo || 'Impossibilidade de atendimento do prazo com qualidade técnica adequada.';
      fundamentacao = fundamentacao || 'Escusa por motivo legítimo, diante de limitação de agenda e compromissos técnicos previamente assumidos.';
      corpo = 'O profissional informa que possui limitações de agenda e compromissos técnicos previamente assumidos que impedem a realização dos trabalhos periciais dentro do prazo fixado ou em prazo compatível com a adequada qualidade técnica da prova. Dessa forma, requer que seja acolhida a presente escusa, com a consequente nomeação de outro profissional.';
    } else if (tipo === 'Impedimento/Suspeição') {
      titulo = 'MANIFESTAÇÃO DE IMPEDIMENTO/SUSPEIÇÃO E ESCUSA DO ENCARGO PERICIAL';
      status = 'Não aceita — impedimento';
      motivo = motivo || 'Existência de circunstância que pode comprometer a imparcialidade.';
      fundamentacao = fundamentacao || 'Manifestação preventiva de impedimento/suspeição, em observância aos deveres de independência, imparcialidade e transparência.';
      corpo = 'Após análise inicial da nomeação, o profissional verificou a existência de circunstância que pode comprometer, ou ao menos aparentar comprometer, a necessária imparcialidade exigida para o exercício da função pericial. A circunstância identificada deverá ser descrita de forma objetiva. Assim, requer o acolhimento da presente manifestação, com a dispensa do encargo e a nomeação de outro perito.';
    } else if (tipo === 'Conflito de interesse') {
      titulo = 'MANIFESTAÇÃO DE CONFLITO DE INTERESSE E ESCUSA DO ENCARGO PERICIAL';
      status = 'Não aceita — conflito de interesse';
      motivo = motivo || 'Possível conflito de interesse relacionado ao objeto, às partes ou ao assunto discutido.';
      fundamentacao = fundamentacao || 'Escusa para preservação da independência e da aparência de imparcialidade técnica.';
      corpo = 'Após análise inicial dos autos, o profissional identificou circunstância que recomenda a não aceitação do encargo, em razão de possível conflito de interesse relacionado ao objeto, às partes ou ao assunto discutido. Requer que seja acolhida a presente escusa, com a nomeação de outro profissional.';
    } else if (tipo === 'Outro motivo justificado') {
      status = 'Não aceita — outro motivo justificado';
      motivo = motivo || 'Outro motivo legítimo a ser detalhado pelo perito.';
      fundamentacao = fundamentacao || 'Escusa por motivo legítimo, a ser submetida à apreciação do Juízo.';
      corpo = 'Embora honrado com a nomeação, o profissional informa a existência de motivo legítimo que inviabiliza a aceitação do encargo pericial neste momento. O motivo deverá ser descrito de forma objetiva. Requer que seja acolhida a presente escusa, com a consequente nomeação de outro profissional.';
    } else {
      status = 'Não aceita — ausência de especialidade técnica';
      motivo = motivo || 'A matéria técnica indicada nos autos não se enquadra adequadamente na área principal de especialização profissional.';
      fundamentacao = fundamentacao || 'Escusa por ausência de especialidade técnica específica para o objeto pericial.';
      corpo = 'Embora honrado com a nomeação, o profissional informa que, após análise inicial do objeto da perícia, verificou que a matéria técnica indicada nos autos não se enquadra adequadamente em sua área principal de especialização profissional. Considerando a necessidade de que a prova pericial seja realizada por profissional com conhecimento técnico específico sobre a matéria controvertida, manifesta-se pela impossibilidade de aceitar o encargo. Requer que seja acolhida a presente escusa, com a consequente nomeação de outro profissional que detenha especialização compatível com o objeto da perícia.';
    }

    const texto = `Excelentíssimo(a) Senhor(a) Doutor(a) Juiz(a) de Direito da ${vara}\n\nProcesso nº: ${numeroProcesso}\nRequerente: ${requerente}\nRequerido: __________\n\nNOME DO PERITO, nomeado para atuar como perito nos autos em epígrafe, vem, respeitosamente, à presença de Vossa Excelência, apresentar ${titulo}.\n\n${corpo}\n\nTermos em que,\nPede deferimento.\n\n__________, ___ de __________ de 20__.\n\nNome do Perito\nContador — CRC nº ________\nE-mail: __________\nTelefone: __________`;

    this.retornoNomeacaoForm = {
      ...this.retornoNomeacaoForm,
      status_nomeacao: status,
      motivo_nao_aceite: motivo,
      fundamentacao,
      texto_manifestacao: texto,
      data_resposta: this.retornoNomeacaoForm.data_resposta || new Date().toISOString().slice(0, 10),
      status_protocolo: this.retornoNomeacaoForm.status_protocolo || 'Rascunho'
    };
  }

  async salvarRetornoNomeacao(): Promise<void> {
    const pericia = this.periciaAtual();
    if (!pericia?.id) {
      this.erro.set('Selecione uma perícia antes de salvar o retorno da nomeação.');
      return;
    }
    this.retornoNomeacaoSalvando = true;
    this.erro.set('');
    this.mensagem.set('');
    try {
      const retorno = await firstValueFrom(this.service.salvarRetornoNomeacao(pericia.id, this.retornoNomeacaoForm));
      this.retornoNomeacaoForm = {
        status_nomeacao: retorno.status_nomeacao || this.retornoNomeacaoForm.status_nomeacao,
        tipo_retorno: retorno.tipo_retorno || this.retornoNomeacaoForm.tipo_retorno,
        data_intimacao: retorno.data_intimacao || '',
        data_resposta: retorno.data_resposta || '',
        motivo_nao_aceite: retorno.motivo_nao_aceite || '',
        fundamentacao: retorno.fundamentacao || '',
        texto_manifestacao: retorno.texto_manifestacao || '',
        status_protocolo: retorno.status_protocolo || 'Rascunho',
        observacoes_internas: retorno.observacoes_internas || ''
      };
      this.mensagem.set('Salvo.');
      if (this.nomeacaoAceita()) {
        this.selecionarAba('Honorários');
        this.carregarHonorarios();
      } else if (!this.abaPermitida(this.abaAtiva())) {
        this.abaAtiva.set('Nomeação');
      }
    } catch {
      this.erro.set('Erro ao salvar o retorno da nomeação. Verifique se o backend está rodando e se a tabela foi criada.');
    } finally {
      this.retornoNomeacaoSalvando = false;
    }
  }

  async visualizarPreviewRetornoNomeacao(): Promise<void> {
    const pericia = this.periciaAtual();
    if (!pericia?.id) {
      this.erro.set('Selecione uma perícia antes de visualizar o preview.');
      return;
    }
    try {
      await this.salvarRetornoNomeacao();
      this.retornoNomeacaoPreviewHtml = await firstValueFrom(this.service.previewRetornoNomeacaoHtml(pericia.id));
      this.retornoNomeacaoPreviewAberto = true;
    } catch {
      this.erro.set('Não foi possível gerar o preview do retorno da nomeação.');
    }
  }

  fecharPreviewRetornoNomeacao(): void {
    this.retornoNomeacaoPreviewAberto = false;
    this.retornoNomeacaoPreviewHtml = '';
  }

  async exportarRetornoNomeacao(): Promise<void> {
    const pericia = this.periciaAtual();
    if (!pericia?.id) {
      this.erro.set('Selecione uma perícia antes de exportar.');
      return;
    }
    await this.salvarRetornoNomeacao();
    this.service.exportarRetornoNomeacao(pericia.id, this.retornoNomeacaoFormatoExportacao || 'pdf');
  }
  private formularioVazio(): PericiaPayload {
    return {
      codigo: '',
      titulo: '',
      tipo_pericia: 'Perícia Contábil',
      cliente: '',
      numero_processo: '',
      vara_ou_camara: '',
      data_inicio: '',
      data_base: '',
      prazo_laudo: '',
      status: 'Planejamento',
      objeto_inicial: '',
      descricao: '',
    };
  }
}
